import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSONContent } from '@tiptap/react'
import {
  ROLE_LABELS,
  type Role,
  adoptWorkspace,
  needsCode,
  api,
  NotSignedIn,
  setAccessLostHandler,
  RequestFailed,
  setCurrentDraft,
  setWorkspaceSlug,
  workspaceSlug,
  activeProject,
  setProject,
  retryDelaySeconds,
  USE_CASE_LABELS,
  type ProjectSummary,
  type ConflictReport,
  type Frontmatter,
  type FrontmatterPatch,
  type Member,
  type OnboardingStatus,
  type Session,
  type State,
  type UseCase,
  type Workspace,
  type Suggestion,
} from './api'
import { drawMermaidDiagrams } from '@staleless/markdown'
import { draftKind, draftLabel, draftTip } from './drafts'
import {
  GLOSSARY_STARTER,
  STYLE_STARTER,
  isSiteFile,
  parseNav,
  serializeNav,
  type Nav,
  type SiteFile,
} from '@staleless/schema'
import {
  addNavSection,
  findNavPath,
  insertNavItem,
  itemAt,
  moveNavItem,
  navPlaces,
  removeNavItem,
  removeNavSection,
  renameNavItem,
  renameNavSection,
  type DropTarget,
  type NavPath,
} from './nav-tree'
import { BlockEditor } from './editor/BlockEditor'
import { WritingHelp } from './components/WritingHelp'
import { contentToMarkdown, markdownToContent } from './editor/markdown-bridge'
import { CommandPalette, type Command } from './components/CommandPalette'
import { CommentsPanel } from './components/CommentsPanel'
import { Notifications } from './components/Notifications'
import { ConfirmIdentity } from './components/ConfirmIdentity'
import { ConflictView } from './components/ConflictView'
import { DiffView } from './components/DiffView'
import { Home } from './components/Home'
import { ImportScreen } from './components/ImportScreen'
import { because } from './said'
import { Onboarding } from './components/Onboarding'
import { PulseScreen } from './components/PulseScreen'
import { AnalyticsScreen } from './components/AnalyticsScreen'
import { LibraryScreen } from './components/LibraryScreen'
import { RedirectsScreen } from './components/RedirectsScreen'
import { VariablesScreen } from './components/VariablesScreen'
import { SettingsScreen } from './components/SettingsScreen'
import { DevelopersScreen } from './components/DevelopersScreen'
import { HelpDocsScreen } from './components/HelpDocsScreen'
import { Deleted, type Deletion } from './components/Deleted'
import { EnrolTwoFactor } from './components/EnrolTwoFactor'
import { SideNav } from './components/SideNav'
import { Inbox } from './components/Inbox'
import { History } from './components/History'
import { Icon } from './components/Icon'
import { CreateDialog } from './components/CreateDialog'
import { Inspector } from './components/Inspector'
import { ReviewDrawer } from './components/ReviewDrawer'
import { PublishSheet } from './components/PublishSheet'
import { MergeConflictView } from './components/MergeConflictView'
import { Shortcuts } from './components/Shortcuts'
import { HelpPanel } from './components/HelpPanel'
import { TooltipLayer } from './components/TooltipLayer'
import { Tour, TOURS } from './components/Tour'
import { Toasts, useToasts } from './components/Toasts'
import { installProductButtonTracking, trackProductEvent } from './product-analytics'
import { TrashDrawer } from './components/TrashDrawer'
import { VerifyBanner } from './components/VerifyBanner'
import { useTheme, type Theme } from './theme'
import { slugFor, PLACE_NAMES, hashFor, parseHash, type Place } from './router'
import { DocHead } from './components/DocHead'
import { SearchScreen } from './components/SearchScreen'
import { WordsScreen } from './components/WordsScreen'

// The Markdown surface is an escape hatch, not the default, so CodeMirror is
// only fetched if someone opens that tab.
const Editor = lazy(() => import('./components/Editor').then((m) => ({ default: m.Editor })))

type View = 'blocks' | 'markdown' | 'preview' | 'diff'
/**
 * `conflict` is its own state, not an error and not dirty.
 *
 * Not `error`, because nothing went wrong and the work is safe. Not `dirty`,
 * because the autosave effect fires on `dirty` — it would retry every few
 * seconds, be refused every time, and warn again each time, which buries
 * everything else on the screen. The next keystroke sets `dirty` again and tries
 * once more, which is exactly when trying again makes sense.
 */
type SaveState = 'clean' | 'dirty' | 'saving' | 'error' | 'conflict' | 'offline'

const NAV_PATH = 'nav.yaml'

/**
 * What the breadcrumb calls each site file.
 *
 * A `Record` over the type rather than a chain of comparisons, so a sixth site
 * file will not compile until somebody has said what it is called. The chain
 * this replaces named three of the six, and the other three showed their path
 * — `search.yaml` at a person, from a door labelled "Open search.yaml", which
 * is the one thing every other naming surface in this product exists to stop.
 */
const SITE_FILE_CRUMBS: Record<SiteFile, string> = {
  'nav.yaml': 'Navigation file',
  'variables.yaml': 'Variables',
  'search.yaml': 'Search',
  'redirects.yaml': 'Redirects',
  'glossary.yaml': 'Words',
  'style.yaml': 'Style',
}
const AUTOSAVE_MS = 1200

/**
 * The four ways to look at a page, and which two are on the bar.
 *
 * `fold` is the ones behind the menu beside it: the Markdown a document is
 * stored as, and the comparison against what readers have. Both are real and
 * both are occasional, and four tabs of equal weight said all four were
 * equally likely. "Compare", not "Diff" — the word a writer uses for reading
 * two things side by side, rather than the word a version control system uses.
 */
const VIEWS: {
  id: View
  label: string
  icon: Parameters<typeof Icon>[0]['name']
  fold?: true
}[] = [
  { id: 'blocks', label: 'Edit', icon: 'blocks' },
  { id: 'preview', label: 'Preview', icon: 'eye' },
  { id: 'markdown', label: 'Markdown', icon: 'code', fold: true },
  { id: 'diff', label: 'Compare', icon: 'diff', fold: true },
]

/**
 * Why the first `/api/state` failed, in the two shapes the boot screen needs
 * to tell apart.
 *
 * `provisioning` is the server's own 503 — the workspace's repository is
 * being created and could not be this time, for a reason it already names.
 * It is not permanent and it is nobody's fault, so it gets a retry rather than
 * the generic "is the API even running" copy. Everything else keeps that
 * generic copy, because it really is the case it was written for: something
 * this page depends on is not reachable at all.
 */
export type BootError =
  | { kind: 'provisioning'; detail: string }
  | { kind: 'draft_unavailable'; branch: string; detail: string }
  | { kind: 'unreachable'; message: string }
  /**
   * Signed in, and in no workspace at all.
   *
   * Nearly unreachable while signing up always made one. Ordinary now: an
   * invited teammate joins somebody else's workspace and owns nothing, so
   * being removed leaves them with an account, an address that cannot sign up
   * again because it is taken, and — until this — a screen saying the server
   * could not be reached, about a server that had answered immediately.
   */
  | { kind: 'no_workspace' }

/** True when the server said this session may not act in that workspace. */
export function notAMember(e: unknown): boolean {
  return e instanceof RequestFailed && e.status === 403 && e.body['code'] === 'not_a_member'
}

/**
 * Read the one signal the server gives for "this is provisioning, not an
 * outage": a 503 from `/api/state`, with a `detail` saying why. Everything
 * else — a network failure, a 500, the dev server not running — is the
 * pre-existing "unreachable" case.
 */
export function classifyBootError(e: unknown): BootError {
  const message = e instanceof Error ? e.message : String(e)
  const serverMessages = [
    message,
    e instanceof RequestFailed && typeof e.body['error'] === 'string' ? e.body['error'] : null,
    e instanceof RequestFailed && typeof e.body['detail'] === 'string' ? e.body['detail'] : null,
  ].filter((value): value is string => Boolean(value))
  const unknownBranch = serverMessages
    .map((value) => value.match(/unknown branch ["'](.+?)["']/i))
    .find((match): match is RegExpMatchArray => Boolean(match))
  if (unknownBranch) {
    return { kind: 'draft_unavailable', branch: unknownBranch[1]!, detail: unknownBranch[0] }
  }
  if (e instanceof RequestFailed && e.status === 503) {
    return { kind: 'provisioning', detail: 'The workspace setup request was not completed.' }
  }
  if (notAMember(e)) return { kind: 'no_workspace' }
  return { kind: 'unreachable', message }
}

/**
 * The published URL for a page. `pages/a/b.md` is served at `/a/b/`, and the
 * root index at `/` — the same mapping the site build uses.
 */
function publishedUrl(siteUrl: string, path: string | null): string {
  const base = siteUrl.replace(/\/$/, '')
  /* A site file is not served anywhere, so the link is to the site itself.
     This named `nav.yaml` alone, so "view this on the site" for `search.yaml`
     pointed at `/search.yaml/`, an address the build publishes for nothing. */
  if (!path || isSiteFile(path)) return base || '/'
  const id = path.replace(/^pages\//, '').replace(/\.md$/, '')
  return id === 'index' ? `${base}/` : `${base}/${id}/`
}

const NEW_PAGE = (title: string) => `---\ntitle: ${title}\n---\n\nWrite the page here.\n`

export default function App() {
  /** A review link must open the tenant/site that created its branch. */
  useState(() => {
    const params = new URLSearchParams(window.location.search)
    const linkedWorkspace = params.get('workspace')
    const linkedProject = params.get('project')
    if (linkedWorkspace) setWorkspaceSlug(linkedWorkspace)
    if (linkedProject) setProject(linkedProject)
  })
  const [state, setState] = useState<State | null>(null)
  /** The workspace's people, for anywhere that has to name one. */
  const [people, setPeople] = useState<Member[]>([])
  /**
   * What the writer has selected in the Markdown editor, for §22's help.
   *
   * Held here rather than in the help panel because the editor reports it and
   * the panel consumes it, and the two are siblings.
   */
  const [selection, setSelection] = useState('')
  /** The one way a suggestion reaches the document; see `Editor`. */
  const editorHandle = useRef<{ replaceSelection: (text: string) => void } | null>(null)
  /**
   * The same for the BLOCK editor, which is where most writing happens.
   *
   * A range and a fragment rather than a string: the suggestion has to land
   * exactly where the selection was, and the fragment is what carries the
   * writer's links and emphasis into the request instead of losing them.
   */
  const [blockSelection, setBlockSelection] = useState<{
    from: number
    to: number
    content: JSONContent[]
    text: string
  } | null>(null)
  const blockHandle = useRef<{
    replaceRange: (from: number, to: number, content: JSONContent[]) => void
  } | null>(null)
  /**
   * A suggestion has been accepted and not yet saved.
   *
   * The next save carries it, and the SERVER writes `ai_draft: true` — §22's
   * "internal AI draft indicator until reviewed". A ref rather than state
   * because `save` reads it, and re-rendering the editor because a marker is
   * pending would put the cursor back where it was a moment ago.
   */
  const aiAccepted = useRef(false)
  /**
   * The revision this save deliberately replaces, when somebody pressed "keep
   * mine" on the conflict screen. A ref for the same reason as the marker above,
   * and one-shot: it belongs to one save.
   */
  const replacing = useRef<string | null>(null)
  const [branch, setBranch] = useState<string | null>(null)
  const [path, setPath] = useState<string | null>(null)
  const [content, setContent] = useState('')
  const [savedContent, setSavedContent] = useState('')
  const [doc, setDoc] = useState<JSONContent | null>(null)
  const [frontmatter, setFrontmatter] = useState<Frontmatter | null>(null)
  const [html, setHtml] = useState('')
  const [diff, setDiff] = useState('')
  const [view, setView] = useState<View>('blocks')
  const [saveState, setSaveState] = useState<SaveState>('clean')

  // Node views and other prop-less callers ask the api module which draft is
  // showing; see setCurrentDraft's comment there.
  useEffect(() => {
    setCurrentDraft(branch)
  }, [branch])
  const [busy, setBusy] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  /** The commit sheet, which names and checkpoints staged work. */
  const [publishOpen, setPublishOpen] = useState(false)
  const [mergeConflict, setMergeConflict] = useState<string[] | null>(null)
  /**
   * Which of the shell's three "make something" questions is on screen.
   *
   * All three were `window.prompt`, and the page one was two of them in a row.
   * See `CreateDialog` at the foot of this file for what that cost.
   */
  const [making, setMaking] = useState<'site' | 'draft' | 'page' | 'section' | null>(null)
  /**
   * Which place the rail has open. Home is the landing screen on purpose: the
   * product's job is saying whether the docs still match reality, and landing
   * in an empty editor said its job was typing.
   */
  const [place, setPlaceState] = useState<Place>(() => {
    /**
     * Back from a vendor's consent screen outranks where you were.
     *
     * A callback is a NAVIGATION: the whole app reloads, and the place below is
     * restored from `localStorage`, which never remembers Settings. So somebody
     * who has just pressed Allow at Linear would land on Home with the answer
     * to what they did sitting unread in the address bar, and would see it only
     * if they happened to walk back to Settings before something cleared it.
     *
     * The parameter is left ON the address deliberately. The connection card is
     * what turns it into a sentence and what removes it afterwards; this only
     * decides which screen is open when it arrives.
     */
    if (new URLSearchParams(window.location.search).has('integration')) return 'settings'
    /**
     * The address outranks the memory. A hash is either a link somebody was
     * sent or the place this browser was standing when it reloaded, and both
     * are more specific than what localStorage remembers.
     */
    const addressed = parseHash(window.location.hash)
    if (addressed) return addressed.place
    /**
     * Where you were survives a reload. Somebody mid-edit who refreshes must
     * land back in the editor, not on Home — losing your place on refresh reads
     * as the app forgetting you. Home stays the landing for a NEW session,
     * which is what the fallback gives.
     */
    try {
      const kept = localStorage.getItem('staleless:place')
      return kept === 'pages' ||
        kept === 'import' ||
        kept === 'library' ||
        kept === 'variables' ||
        kept === 'analytics'
        ? kept
        : 'home'
    } catch {
      return 'home'
    }
  })
  /**
   * Where we are, readable without depending on it.
   *
   * `refresh` asks for the Pulse counts only on Home, and every screen in this
   * file depends on `refresh` — so reading `place` there would rebuild the
   * callback on every navigation and re-run half the effects that hold it.
   */
  const placeRef = useRef<Place>(place)
  useEffect(() => {
    placeRef.current = place
  }, [place])
  useEffect(() => installProductButtonTracking(() => placeRef.current), [])

  const setPlace = useCallback((next: Place) => {
    setPlaceState(next)
    placeRef.current = next
    try {
      localStorage.setItem('staleless:place', next)
    } catch {
      // Forgetting between visits is the only cost.
    }
    /**
     * Navigation writes the address, as an ENTRY: assigning `location.hash`
     * pushes onto history, which is what makes the browser's Back walk the
     * places. Skipped when the hash already names this place, so re-pressing
     * a row does not bury Back under copies of where you already are.
     */
    if (parseHash(window.location.hash)?.place !== next) {
      window.location.hash = hashFor(next)
    }
  }, [])
  /**
   * The page a link asked for, spent once when the first state arrives.
   * A ref rather than state: it renders nothing, and it must survive the
   * renders between boot and the tree existing.
   */
  const wantedPage = useRef<string | null>(parseHash(window.location.hash)?.pageId ?? null)
  /**
   * MCP and other agents can hand a reviewer a private writer link. The draft
   * lives in the query because the hash already belongs to the page address.
   * It is consumed by the first state request and never changes the live site.
   */
  const wantedDraft = useRef<string | null>(
    new URLSearchParams(window.location.search).get('draft'),
  )
  /**
   * The sidebar, folded to its 48px strip. A per-browser convenience, kept
   * the way the theme is kept — losing it costs a keystroke (⌘\).
   */
  const [folded, setFoldedState] = useState(() => {
    try {
      return localStorage.getItem('staleless:sidenav') === 'folded'
    } catch {
      return false
    }
  })
  const setFolded = useCallback((next: boolean) => {
    setFoldedState(next)
    try {
      localStorage.setItem('staleless:sidenav', next ? 'folded' : 'open')
    } catch {
      // Forgetting between visits is the only cost.
    }
  }, [])
  /**
   * At phone width the sidebar is a drawer behind the top bar's menu button;
   * this is whether it is out. Never persisted — a drawer that reopens itself
   * over the content on every load is a wall, not a memory.
   */
  const [navOpen, setNavOpen] = useState(false)
  /**
   * The place a new page should be listed under, when the question was asked
   * from a ROW rather than from the foot of the tree.
   *
   * Held beside the dialog rather than passed through it: the dialog asks for
   * a place of its own, and this only decides which one it opens on.
   */
  const [placeUnder, setPlaceUnder] = useState<NavPath | null>(null)
  /**
   * Back and Forward. The browser moves the hash; this makes the shell agree
   * with it. Registered once — it reads nothing but the event.
   */
  useEffect(() => {
    const onHash = () => {
      const parsed = parseHash(window.location.hash)
      if (!parsed) return
      try {
        localStorage.setItem('staleless:place', parsed.place)
      } catch {
        // Forgetting between visits is the only cost.
      }
      setPlaceState(parsed.place)
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  const [inboxOpen, setInboxOpen] = useState(false)
  /**
   * The settings section the shell has asked for, if any.
   *
   * People stopped being a drawer over whatever somebody was writing: it is a
   * screen in the organisation's own settings, beside the plan that decides
   * how many people there may be. Every door that used to open the drawer —
   * the sidebar row, the switcher's invite item, Home's checklist, the
   * palette — asks for that section instead.
   */
  const [settingsAt, setSettingsAt] = useState<string | null>(null)
  const openPeople = useCallback(() => {
    setSettingsAt('org/people')
    setPlace('settings')
  }, [])
  const [historyOpen, setHistoryOpen] = useState(false)
  const openHistory = useCallback(() => {
    trackProductEvent('history.opened', { surface: 'history' })
    setHistoryOpen(true)
  }, [])
  const [trashOpen, setTrashOpen] = useState(false)
  /**
   * How many suggestions are open, for the badge.
   *
   * Loaded once on boot rather than polled. A number that ticks up while
   * somebody is writing is a distraction from the thing they came here to do,
   * and the scan that produces suggestions runs on a schedule, not per
   * keystroke.
   */
  const [inboxCount, setInboxCount] = useState(0)
  /** The open suggestions behind that count, so Home does not ask again. */
  const [openFindings, setOpenFindings] = useState<Suggestion[] | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  /** The panel about the screen somebody is on; `?` and the top bar open it. */
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)
  /**
   * The guided tours this person has already been shown, and the one running.
   *
   * `seenTours` is null until the server has said — which matters, because
   * "we do not know yet" must not look like "they have seen nothing" and
   * start a tour over somebody's work on the first frame.
   */
  /** Whether the two folded views are showing. See `VIEWS`. */
  const [viewMenu, setViewMenu] = useState(false)
  const [seenTours, setSeenTours] = useState<string[] | null>(null)
  const [tourRunning, setTourRunning] = useState<string | null>(null)
  /** Dismissed for this visit only — it comes back on reload while unverified. */
  const [verifyDismissed, setVerifyDismissed] = useState(false)
  const [bootError, setBootError] = useState<BootError | null>(null)
  /**
   * The server has just said this session may not act where it is acting.
   *
   * A flag rather than the handling itself: the request that discovers it is
   * made from a callback declared before the workspace switch exists, and the
   * answer needs both.
   */
  const [lostAccess, setLostAccess] = useState(false)
  /**
   * The workspace, or the account, has just been deleted from Settings.
   *
   * It outranks every other render below — including `lostAccess`, which is
   * what the deletion causes a second later. Losing access because you deleted
   * the thing yourself is not the same event as being taken out of a team, and
   * the shell's answer to the second (switch workspaces, or show the gate) is
   * the wrong answer to the first: it would replace the only statement of the
   * grace period this product makes with a toast about somebody else's
   * workspace.
   */
  const [deleted, setDeleted] = useState<Deletion | null>(null)
  /**
   * Whether this session was just created by the sign-up form, rather than an
   * existing one signing back in.
   *
   * The only reason the boot screen needs this: a fresh workspace is
   * provisioning a real repository behind the scenes, which can take several
   * seconds, and a returning person's boot is just a read. Saying "Setting up
   * your workspace" to someone who has used this workspace for months would be
   * false; saying nothing to someone who just signed up is the defect this
   * exists to fix.
   */
  const [justSignedUp, setJustSignedUp] = useState(false)
  // The session is the server's answer to /auth/me, not something this page
  // holds. `undefined` means we have not asked yet, `null` means signed out.
  const [session, setSession] = useState<Session | null | undefined>(undefined)
  /**
   * Where the guided first run stands, for the workspace on screen.
   *
   * The server's answer, never derived here: only it knows who created the
   * workspace and whether the run already ended, so a second browser — or a
   * teammate who joined by invitation — is refused by the same authority that
   * recorded the answers. `undefined` means not asked yet, and the boot
   * skeleton holds until it resolves: flashing Home at somebody the sequence
   * is about to replace would read as the app changing its mind. `null` means
   * the question could not be answered, and the app simply proceeds — a lost
   * nicety, never a wedge.
   */
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null | undefined>(undefined)
  /** Which workspace we are acting in. Only ever one this person belongs to. */
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  /**
   * The documentation sites in this workspace, and the one on screen.
   *
   * A workspace holds several — Adalo would keep Ada, Classic and Studio apart,
   * each with its own repository — and the API has said so for a while with
   * nothing in the interface to show it. So a person could only ever reach the
   * first one.
   */
  const [projects, setProjects] = useState<ProjectSummary[]>([])
  /**
   * Whether the plan includes another documentation site (§55).
   *
   * The server's answer, not a count compared here: the rule belongs to the
   * route that would refuse it. Defaults to true so nothing is hidden while the
   * list is still loading, or against a deployment that does not say.
   */
  const [mayAddSite, setMayAddSite] = useState(true)
  const [project, setProjectState] = useState<string | null>(activeProject())
  const [theme, setTheme] = useTheme()
  const toasts = useToasts()
  const openInbox = useCallback(() => {
    trackProductEvent('inbox.opened', { surface: 'inbox' })
    setInboxOpen(true)
  }, [])

  /**
   * A branch that has been published cannot be written to, and the server says
   * so on the first keystroke: "branch is merged; start a new one". The editor
   * used to let somebody type into one and then show "Save failed", which reads
   * as a broken product rather than a finished branch.
   */
  const closed = state?.branches.find((b) => b.name === branch)?.status
  /**
   * A viewer may read and comment, and not edit (spec 7).
   *
   * Same treatment as a finished branch, and for the same reason the comment
   * above gives: the server refuses the write, so offering the keystroke first
   * and explaining afterwards reads as a broken product. It goes through the one
   * `readOnly` flag the rest of this file already consults, so the palette
   * actions, the branch picker, the Blocks tab and every disabled control follow
   * without being told separately.
   */
  const role = state?.you?.role ?? null
  /**
   * The role in the workspace, which is a different question from `role`.
   *
   * `role` is the standing in the documentation site on screen, with this
   * project's override applied — the right answer for editing, publishing and
   * everything else about the site. The workspace's own decisions (make a docs
   * site, invite somebody, read the audit trail) are not about this site, and
   * reading them off the override made a project role into a promotion. Falls
   * back to `role` so an older server answers as it always did.
   */
  const workspaceRole = state?.you?.workspaceRole ?? state?.you?.role ?? null
  /**
   * Where THIS project's published site answers.
   *
   * A workspace's second documentation site answers under its own path, so one
   * address for the whole deployment sent "View site" to the first project's
   * site from every other one — the site it is easiest to mistake it for. The
   * project list carries each one's address, computed by the same function the
   * builder bakes into the pages; `state.siteUrl` is the fallback for a server
   * that does not send one yet.
   */
  const siteAddress = projects.find((p) => p.slug === project)?.siteUrl ?? state?.siteUrl ?? null
  /**
   * The version this draft is for, as a reader would name it, or null.
   *
   * Read from the branch list rather than held separately: `/api/state`
   * already says which ref each draft targets, and a second copy of that
   * would be a second thing to keep in step.
   */
  const aimedAt = (() => {
    const row = state?.branches.find((b) => b.name === branch)
    const ref = row?.targetRef
    return ref ? ref.replace(/^version\//, '') : null
  })()

  /**
   * Whether the editor is inert.
   *
   * NOT "there is no draft" any more. A draft is how this product keeps
   * unpublished work apart from the live site, and that is a fact about
   * storage, not a question a writer should be made to answer before typing:
   * the old rule made the live site read-only, so the first thing anybody met
   * was an editor that refused them and a button called "Start writing" whose
   * job was to explain a branching model. The draft is made on the first
   * keystroke now — see `save` — and named after the person and the day,
   * which is what `startWriting` always did.
   *
   * What still makes the editor read-only: somebody who may not write at all,
   * and a draft that is finished. Both are refusals the server would make.
   */
  const readOnly = closed === 'merged' || closed === 'closed' || role === 'viewer'
  useEffect(() => {
    if (!workspace?.slug || !project) return
    trackProductEvent('screen.view', {
      surface: place,
      properties: {
        branch: branch ? 'draft' : 'live',
        read_only: readOnly,
        project_role: role,
        settings: place === 'settings' ? (settingsAt ?? 'site/general') : null,
        workspace_role: workspaceRole,
      },
    })
  }, [branch, place, project, readOnly, role, settingsAt, workspace?.slug, workspaceRole])
  /**
   * The revision this editor is working against.
   *
   * A ref, not state: the save loop reads it and must see the current value, and
   * it must not cause a render. Set when a page is opened and after every
   * successful save — a save that did not adopt the new revision would conflict
   * with itself on the next keystroke.
   */
  const revision = useRef<string | null>(null)
  /**
   * One save at a time.
   *
   * Two could overlap before — the autosave timer firing while an earlier save
   * was still in flight — and it was merely wasteful, because the last write
   * won. With revisions it is a conflict: the second save carries the revision
   * the first one has already superseded, the server refuses it, and the page
   * sits on "Unsaved" with its work stranded. The editor gate caught exactly
   * that, at the typing speeds where the timer lands mid-sentence.
   *
   * So a save requested during a save sets `again`, and the one in flight starts
   * it on the way out. Relying on the state going back to `dirty` does not work:
   * it is already `dirty`, and React does not re-run an effect for a value that
   * did not change — which would leave nothing scheduled at all.
   */
  const saving = useRef(false)
  const again = useRef(false)
  const pendingDoc = useRef<JSONContent | null>(null)
  const pendingText = useRef<string | null>(null)

  const refresh = useCallback(
    async (b: string | null = branch) => {
      try {
        /*
         * Ask for the Pulse counts in the same breath, but only where they are
         * shown.
         *
         * Home used to fetch them itself, which meant painting it read the
         * whole content tree twice — once here for the page tree and the lint,
         * once for `/api/health` — in two requests a millisecond apart. A tree
         * read is about 60ms of git process spawn whatever it holds.
         *
         * `placeRef` rather than `place`, so this callback does not have to be
         * rebuilt on every navigation: every screen depends on `refresh`, and
         * making it change identity whenever somebody moves would re-run half
         * the effects in this file.
         */
        const next = await api.state(
          b ?? undefined,
          placeRef.current === 'home' ? 'health' : undefined,
        )
        setState(next)
        setBootError(null)
        // Consumed: the flag only matters for the FIRST successful load after
        // signing up. Leaving it set would tell a later sign-in "setting up
        // your workspace" for a workspace that has existed for months.
        setJustSignedUp(false)
        return next
      } catch (e) {
        /**
         * A sign-out can race this read. The server's 401 is the correct
         * answer, not an outage: clear the stale workspace screen and show the
         * sign-in door. This used to feed `not signed in` to the generic boot
         * classifier, producing “Cannot reach the API” immediately after the
         * person had deliberately signed out.
         */
        if (e instanceof NotSignedIn) {
          setState(null)
          setBootError(null)
          setSession(null)
          return null
        }
        /**
         * Removed from the workspace on screen, or in none at all.
         *
         * Not an outage, and it used to be reported as one — "could not reach
         * the server", about a server that answered immediately and
         * correctly. The session is re-read below, because only the server
         * knows what is left; doing it here would make this callback depend on
         * the switch it has to perform.
         */
        if (notAMember(e)) {
          setLostAccess(true)
          return null
        }
        if (!state) setBootError(classifyBootError(e))
        else
          toasts.error(
            'Could not reach the server',
            because(
              e,
              `Nothing you have written is lost — this browser is keeping it. It tries again on its own; reload the page if it keeps failing.`,
            ),
          )
        return null
      }
    },
    // `toasts` and `state` are stable enough for this; refreshing is idempotent.
    [branch], // eslint-disable-line react-hooks/exhaustive-deps
  )

  /**
   * Load the workspace's projects.
   *
   * On boot and after a workspace switch, because a project slug means nothing
   * outside the workspace it belongs to.
   */
  /**
   * Which tours this person has already been shown.
   *
   * Read once per session. A failure is treated as "they have seen them all"
   * rather than "they have seen none": the cost of not offering a tour is
   * small, and the cost of walking somebody around a screen they know every
   * time the endpoint hiccups is the product being annoying on purpose.
   */
  useEffect(() => {
    if (!session) return
    let cancelled = false
    void api
      .tours()
      .then((result) => {
        if (!cancelled) setSeenTours(result.seen)
      })
      .catch(() => {
        if (!cancelled) setSeenTours(Object.keys(TOURS))
      })
    return () => {
      cancelled = true
    }
  }, [session])

  /**
   * Offer the tour for the screen somebody has just arrived at, once.
   *
   * Held back until the first run is finished and nothing else is in the way:
   * a tour that starts over a dialog points at controls nobody can reach, and
   * one that starts during the first run is two guides talking at once.
   */
  useEffect(() => {
    if (!session || !state || seenTours === null || tourRunning) return
    if (onboarding?.shouldOnboard) return
    // Only the things that would be TALKED OVER: the first run, a dialog that
    // owns the keyboard, and Help, which is somebody already asking.
    if (making || helpOpen || helpPanelOpen || paletteOpen) return
    /*
     * The topmost thing on the screen, not the place behind it.
     *
     * A drawer is a surface too — the changes drawer and the inbox each carry
     * controls whose relationship is not obvious — and both used to be in the
     * list of reasons NOT to offer a tour, which meant the tours written for
     * them were never once offered to anybody.
     *
     * The publish sheet is the exception, and deliberately: it is opened by
     * somebody who has decided to publish, and a walk that starts while they
     * are reading what will change is a walk over the top of the one decision
     * this product asks people to take carefully. Its tour lives in Help.
     */
    const open = publishOpen ? null : reviewOpen ? 'review' : inboxOpen ? 'inbox' : null
    /*
     * The PLACE's tour, if `TOURS` has one — rather than a chain naming the
     * places somebody remembered.
     *
     * The chain this replaces named `pages`, `pulse` and `settings`, and Home
     * was not among them: the screen everybody lands on was the one screen
     * with no walk, because the tours were written for the screens with
     * obvious controls and Home has none. Reading `TOURS` means a tour written
     * for a place is offered on the day it is written, which is the whole
     * reason the tours live in one table.
     */
    /*
     * A place's tour, unless that tour asks to be asked for. `askedFor` is on
     * the tour rather than in a list here, so the decision travels with the
     * walk it is about — see Home's, which carries the reason.
     */
    const forPlace = TOURS[place]?.askedFor ? null : TOURS[place] ? place : null
    const surface = open ?? forPlace
    if (!surface || seenTours.includes(surface)) return

    /*
     * A beat, so the screen it is about has finished arriving — and CANCELLED
     * the moment somebody starts doing something.
     *
     * A tour that dims the screen a second after you have begun clicking is
     * an interruption, not guidance: it takes the keyboard, covers the thing
     * you were reaching for, and makes the product feel like it is talking
     * over you. Somebody who is already working does not need to be told
     * where anything is, and Help still has the tour when they do.
     */
    const soon = window.setTimeout(() => setTourRunning(surface), 900)
    const cancel = () => window.clearTimeout(soon)
    window.addEventListener('pointerdown', cancel, { once: true })
    window.addEventListener('keydown', cancel, { once: true })
    return () => {
      window.clearTimeout(soon)
      window.removeEventListener('pointerdown', cancel)
      window.removeEventListener('keydown', cancel)
    }
  }, [
    session,
    state,
    seenTours,
    tourRunning,
    onboarding,
    place,
    making,
    reviewOpen,
    publishOpen,
    inboxOpen,
    helpOpen,
    helpPanelOpen,
    paletteOpen,
  ])

  /**
   * Remember a tour — and tell one somebody WATCHED from one they waved away.
   *
   * `Tour` has always reported which it was; this threw the answer away, so a
   * walk watched to the end and a walk dismissed by a stray click on the
   * dimming were recorded identically and neither was ever offered again. The
   * dimming is what makes it worth a migration: one click outside the card,
   * which is how every other overlay here is dismissed, retired that person's
   * guidance for ever.
   *
   * A finish retires it. A dismissal is recorded AS a dismissal and gets one
   * more chance on a later visit — not this one, because re-offering a walk
   * somebody has just waved away is the nag every comment around this is
   * written against. The second dismissal is an answer rather than an
   * accident, and the server retires it then.
   *
   * On the server rather than in this browser, which is the property the
   * guidance gate already asserts and the right one: the same person on their
   * laptop and their desk machine is one person, and a middle state kept in
   * one browser would offer the tour again on the other.
   */
  function endTour(surface: string, finished: boolean) {
    setTourRunning(null)
    // Not again this visit either way. `seenTours` is the in-memory gate; what
    // the server records decides whether there is a next time.
    setSeenTours((seen) => (seen?.includes(surface) ? seen : [...(seen ?? []), surface]))
    void api.markTourSeen(surface, { finished }).catch(() => {
      // Nothing to say: the worst case is being offered it once more.
    })
  }

  useEffect(() => {
    let cancelled = false
    if (!session) return
    void api
      .projects()
      .then((result) => {
        if (cancelled) return
        setProjects(result.projects)
        setMayAddSite(result.mayAddAnother ?? true)
        // Adopt the server's answer when we had no remembered choice, so the
        // picker shows what the request actually acted in.
        setProjectState((current) => current ?? result.active)
      })
      .catch(() => {
        // A workspace still being provisioned has no projects to list yet. The
        // boot path already reports that failure; this must not report it twice.
      })
  }, [session, workspace])

  /**
   * Add a docs site to this workspace.
   *
   * A dialog rather than a form screen: the only input is a name, the
   * repository is derived and created server-side, and the answer arrives in
   * seconds. A whole screen for one field would be ceremony.
   */
  const createProject = useCallback(async (name: string) => {
    const made = await api.createProject(name.trim())
    const next = await api.projects()
    setProjects(next.projects)
    setMayAddSite(next.mayAddAnother ?? true)
    toasts.success(`${made.project.name} exists`, 'Its own repository, its own published site.')
    switchProject(made.project.slug)
    setPlace('pages')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Move to another project.
   *
   * Everything open belongs to the project being left — the branch, the page,
   * the staged edits — so it all resets, exactly as switching workspace does.
   */
  const switchProject = useCallback(
    (slug: string) => {
      if (!slug || slug === project) return
      setProject(slug)
      setProjectState(slug)
      setPath(null)
      setBranch(null)
      void refresh(undefined)
    },
    [project], // eslint-disable-line react-hooks/exhaustive-deps
  )

  /**
   * Move to another workspace.
   *
   * Everything currently open belongs to the workspace being left — the branch,
   * the page, its staged edits, the diff. Carrying any of it across would ask
   * the server for a path that does not exist there or, far worse, write to a
   * same-named path that does. So this clears the editor rather than trying to
   * keep the reader's place.
   */
  const switchWorkspace = useCallback(
    (slug: string) => {
      if (!slug || slug === workspaceSlug()) return
      const next = session?.workspaces?.find((w) => w.slug === slug)
      // Only ever a workspace the server said we belong to; the select is built
      // from that same list, so this is a guard rather than a branch.
      if (!next) return

      setWorkspaceSlug(slug)
      /**
       * The docs site goes with it.
       *
       * `setWorkspaceSlug` has already adopted whatever was remembered for the
       * workspace being entered — or nothing, which means "the one the server
       * acts in by default". This state is what the picker DISPLAYS, and it
       * was the one thing not reset here: it kept naming the site that was
       * open in the workspace being left. Usually harmless, because a slug
       * from elsewhere matches no option and the picker falls back to the
       * first — but two workspaces with a site called "Handbook" is not
       * exotic, and then the picker named a real site while every request
       * acted in a different one.
       */
      setProjectState(activeProject())
      setWorkspace(next)
      setBranch(null)
      setPath(null)
      setContent('')
      setSavedContent('')
      setDoc(null)
      setFrontmatter(null)
      setHtml('')
      setDiff('')
      setSaveState('clean')
      pendingDoc.current = null
      pendingText.current = null
      void refresh(null)
    },
    [session, refresh],
  )

  /**
   * Any request, anywhere, that comes back "not a member".
   *
   * Registered once rather than handled per call: the discovery arrives at
   * whichever request happens next, and only `/api/state` used to notice.
   */
  useEffect(() => {
    setAccessLostHandler(() => setLostAccess(true))
    return () => setAccessLostHandler(null)
  }, [])

  /**
   * What to do about it, once something has said "not here".
   *
   * Ask the server what this account still belongs to, because that is the
   * only place the answer exists — a membership can be taken away between two
   * requests, and a workspace can be deleted underneath somebody reading it.
   * Then either move them and say so, or say plainly that there is nowhere
   * left, which is a real state now that an invited teammate owns nothing.
   */
  useEffect(() => {
    if (!lostAccess) return
    let cancelled = false
    const left = workspace?.name
    void api
      .me()
      .then((next) => {
        if (cancelled) return
        setLostAccess(false)
        setSession(next)
        const list = next.workspaces ?? []
        if (list.length === 0) {
          setWorkspace(null)
          setBootError({ kind: 'no_workspace' })
          return
        }
        const moved = list[0]!
        // Said out loud. Somebody whose screen changes workspace without a
        // word assumes they clicked something.
        toasts.error(
          left ? `You are no longer in ${left}` : 'That workspace is not yours to open',
          `Showing ${moved.name} instead.`,
        )
        setBootError(null)
        switchWorkspace(moved.slug)
      })
      .catch(() => {
        // The session itself is gone. The gate is the right answer, and it
        // reports its own failures.
        if (cancelled) return
        setLostAccess(false)
        setSession(null)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lostAccess])

  /** Ask the server who we are. The cookie does the work; nothing is stored here. */
  const loadSession = useCallback(async () => {
    try {
      const next = await api.me()
      // Before setState, because setting the session triggers the first content
      // request and that request needs to name a workspace. Adopting after it
      // would read the arbitrary first membership once on every boot.
      setWorkspace(adoptWorkspace(next))
      setSession(next)
    } catch {
      // Any failure to identify is "signed out". A gate is the right answer to
      // both an expired session and an unreachable server, and the gate itself
      // reports the second case when signing in fails.
      setSession(null)
    }
  }, [])

  useEffect(() => {
    void loadSession()
  }, [loadSession])

  /**
   * Who is in this workspace, for the inspector's owner list.
   *
   * Once per workspace rather than per page: the rail remounts whenever
   * somebody selects a page, and a request per selection would be a request
   * per click for a list that changes when somebody joins.
   */
  useEffect(() => {
    if (!session) {
      setPeople([])
      return
    }
    void api
      .members()
      .then((answer) => setPeople(answer.members))
      // An owner list that cannot be fetched is an empty list, and the field
      // then offers only what the page already says. Not a reason to fail.
      .catch(() => setPeople([]))
  }, [session, workspace])

  useEffect(() => {
    if (session) {
      const requested = wantedDraft.current
      if (requested) setBranch(requested)
      void refresh(requested)
    }
  }, [session]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Ask again from the top: on boot, after a workspace switch, on boot retry. */
  const loadOnboarding = useCallback(async () => {
    try {
      setOnboarding(await api.onboarding())
    } catch {
      // Not knowing must not block the app; the worst loss is the sequence
      // itself, which the same person gets on their next visit.
      setOnboarding(null)
    }
  }, [])

  useEffect(() => {
    if (!session) return
    // Back to "not asked" first: a workspace switch must not show — or
    // suppress — this workspace's first run based on the previous one's answer.
    setOnboarding(undefined)
    void loadOnboarding()
  }, [session, workspace, loadOnboarding])

  /**
   * An invitation link, if they arrived on one.
   *
   * Read once and held, because the URL is cleaned immediately: a capability
   * sitting in the address bar gets copied into chat messages and bug reports,
   * and this one is worth exactly one workspace.
   */
  const [invitation, setInvitation] = useState<string | null>(() => {
    const found = new URLSearchParams(window.location.search).get('invitation')
    if (found) {
      const url = new URL(window.location.href)
      url.searchParams.delete('invitation')
      window.history.replaceState({}, '', url.toString())
    }
    return found
  })

  /**
   * A confirmation link, if they arrived on one.
   *
   * `/verify?token=…` is the address in the email this product sends, and
   * nothing here read it. The banner could ask for a link and the API could
   * spend one, but the page the link opened ignored it — so an address could
   * not be confirmed through the product at all, and publishing, which is
   * gated on a confirmed address, could not be reached. It stayed hidden
   * because every test that needed a verified account set the column directly.
   *
   * Read once and taken out of the address bar, like the invitation below: a
   * link that confirms an identity does not belong in a copied URL.
   */
  /**
   * A link that moves the address this account signs in with.
   *
   * Read here rather than in the sign-in gate because it may be opened in the
   * browser that is already signed in — where the gate is not on screen. It
   * ends every session, so what follows is the gate either way, and the new
   * address is handed to it so somebody is told what to sign in with.
   */
  const [moving, setMoving] = useState<string | null>(() => {
    if (window.location.pathname !== '/email') return null
    const found = new URLSearchParams(window.location.search).get('token')
    if (found) window.history.replaceState({}, '', '/')
    return found
  })
  const [movedTo, setMovedTo] = useState<string | null>(null)
  /**
   * A session that ended while somebody was writing, so the gate can say so.
   *
   * Not persisted: it describes this visit to the sign-in, and a reload has
   * already lost the moment it is about to explain. The kept copy is what
   * survives, and it is kept somewhere else.
   */
  const [sessionEnded, setSessionEnded] = useState(false)
  const movedOnce = useRef<string | null>(null)
  useEffect(() => {
    if (!moving || movedOnce.current === moving) return
    movedOnce.current = moving
    void api
      .confirmEmailChange(moving)
      .then(async (done) => {
        setMoving(null)
        setMovedTo(done.email)
        // The session is gone with every other one, so this brings the gate up.
        await loadSession()
      })
      .catch((e: unknown) => {
        setMoving(null)
        toasts.error(
          'That link did not work',
          `${e instanceof Error ? e.message : 'The link may have expired.'} Ask for a new one in Settings.`,
        )
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moving])

  const [confirming, setConfirming] = useState<string | null>(() => {
    if (window.location.pathname !== '/verify') return null
    const found = new URLSearchParams(window.location.search).get('token')
    if (found) window.history.replaceState({}, '', '/')
    return found
  })

  /**
   * Spent once, even in development.
   *
   * The token is one-time, and StrictMode runs an effect twice — which would
   * spend it on the first run and then report the second failure to somebody
   * whose address was, in fact, just confirmed.
   */
  const confirmed = useRef<string | null>(null)
  useEffect(() => {
    if (!confirming || confirmed.current === confirming) return
    confirmed.current = confirming
    let cancelled = false
    void api
      .confirmEmail(confirming)
      .then(async () => {
        if (cancelled) return
        setConfirming(null)
        toasts.success('Address confirmed', 'You can publish and push a draft now.')
        // So the banner goes, and the publish gate opens, without a reload.
        await loadSession()
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setConfirming(null)
        toasts.error(
          'That link did not work',
          `${e instanceof Error ? e.message : 'The link may have expired.'} Ask for a new one from the banner at the top.`,
        )
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirming])

  /**
   * Spend it, once they are signed in.
   *
   * Held rather than spent on arrival, because an invited person usually has no
   * account yet: they sign up, and only then is there somebody for the
   * invitation to admit. The address is checked server-side, so signing up under
   * a different address fails with a message that says exactly that.
   */
  useEffect(() => {
    if (!session || !invitation) return
    let cancelled = false
    void api
      .acceptInvitation(invitation)
      .then(async (result) => {
        if (cancelled) return
        setInvitation(null)
        if (result.workspace) {
          setWorkspaceSlug(result.workspace.slug)
          setWorkspace(result.workspace)
          toasts.success(`You are in ${result.workspace.name}`, 'Somebody invited you here.')
          // Re-read the session so the switcher shows the new workspace, then
          // load its content.
          setSession({ ...session, workspaces: result.workspaces })
          await refresh(null)
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setInvitation(null)
        toasts.error(
          'That invitation did not work',
          `${e instanceof Error ? e.message : 'It may have been used already.'} Ask whoever invited you to send another.`,
        )
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, invitation])

  useEffect(() => {
    if (!session) return
    // A failure here is silent on purpose. An unreadable inbox count is not a
    // reason to interrupt somebody, and the drawer reports its own errors when
    // they actually open it.
    void api
      .inbox(['open'])
      .then((body) => {
        setInboxCount(body.counts['open'] ?? 0)
        /*
         * Kept, not counted and thrown away.
         *
         * Home asked for the inbox again — the whole thing rather than the
         * open ones — to show the same count and the findings behind it, so a
         * cold load fetched it twice for two readers of one answer. This is
         * the narrower question of the two, and it already carries what Home
         * needs.
         */
        setOpenFindings(body.suggestions)
      })
      .catch(() => undefined)
  }, [session, workspace])

  // ---------------------------------------------------------------- saving

  /**
   * Autosave.
   *
   * Safe here in a way it would not be in most editors, because a save STAGES —
   * it writes a Postgres row, not a git object. Nothing enters history until
   * someone commits, so the cost of saving early is nil and the cost of losing
   * a paragraph is real. ⌘S still works for people who want to feel it.
   */
  /**
   * Save, without losing anything typed while the save was happening.
   *
   * Two round trips happen here — the write, then a re-read, because the server
   * canonicalises and this is where the editor learns what actually landed on
   * disk. Somebody typing at a normal speed produces several changes inside that
   * window, and both of them used to be discarded.
   *
   * The pending buffer was cleared unconditionally after the write, so an edit
   * made during the request stopped being pending and was never saved. Then the
   * re-read was applied to the editor, so that same edit was wiped off the
   * screen as well. Lost twice, silently, and more likely the faster somebody
   * types.
   *
   * So the pending buffer is only cleared if it still holds exactly what was
   * sent, and the re-read is only applied if the editor has not moved on. When
   * it has, the state goes back to dirty and the next save carries the newer
   * work — the server's answer is simply out of date, which is not an error.
   */
  const save = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!path) return
      const sentDoc = pendingDoc.current
      const sentText = pendingText.current
      if (sentDoc === null && sentText === null) return
      if (saving.current) {
        // Run it when the current one is done, with whatever is pending then.
        again.current = true
        return
      }
      saving.current = true

      /**
       * The draft, made on the first keystroke rather than asked for.
       *
       * Writing has always happened on one — it is what keeps unpublished work
       * off the live site — and the product used to make that the writer's
       * problem: an editor that refused to take a character until somebody had
       * found the control, understood the model, and named a branch. This makes
       * one at the only moment it is actually needed, named after the person
       * and the day. `saving.current` is already true, so nothing else can
       * start a second one while this is in flight.
       */
      let on = branch
      if (!on) {
        on = await startWriting({ silent: true })
        if (!on) {
          // `startWriting` has already said what went wrong.
          saving.current = false
          setSaveState('error')
          return
        }
      }

      /** Identity, not equality: this asks "is this still the same edit?" */
      const movedOn = () => pendingDoc.current !== sentDoc || pendingText.current !== sentText

      setSaveState('saving')
      try {
        const base = revision.current ?? undefined
        /**
         * Cleared before the write, not after.
         *
         * The write is what carries it, and a second save while this one is in
         * flight must not mark the page again — the marker is already on the
         * page by then, and re-sending it would set it on a save the writer
         * made by hand.
         */
        const accepted = aiAccepted.current
        aiAccepted.current = false
        /**
         * And whether this save is an overwrite chosen on the conflict screen.
         *
         * One-shot, cleared before the write for the same reason as the marker
         * above: the person whose version is being replaced should be told once,
         * about the save that did it, and not again by the next autosave.
         */
        const replaced = replacing.current
        replacing.current = null
        const out =
          /* And the same question a third time, because this is the call that
             would do the damage: a document serialised over YAML. The server
             refuses it too — this keeps the writer from asking. */
          sentDoc !== null && !isSiteFile(path)
            ? await api.writeDoc(on, path, sentDoc, base, accepted, replaced ?? undefined)
            : await api.write(on, path, sentText ?? content, base, accepted, replaced ?? undefined)
        // Adopted immediately, before any of the checks below can return early.
        // A save that kept the old revision would be refused by the NEXT save as
        // stale against its own work.
        revision.current = out.revision

        if (movedOn()) {
          // Keep the newer edit pending and stay dirty, so the autosave fires
          // again for it. What we sent did land; the editor is simply ahead.
          setSaveState('dirty')
          again.current = true
          await refresh(on)
          return
        }

        const page = await api.page(path, on)
        // Checked again: the re-read is a second round trip, and somebody can
        // type during that one too.
        if (movedOn()) {
          setSaveState('dirty')
          again.current = true
          await refresh(on)
          return
        }

        // Cleared only now, after the last check. Clearing it earlier made
        // `movedOn` compare null against what was sent and report that the
        // editor had moved on — for ever. The state stayed dirty, and the next
        // autosave found nothing pending and returned, so a page could sit on
        // "Unsaved" indefinitely with its work already safely on disk.
        pendingDoc.current = null
        pendingText.current = null
        dropCopy(path)
        setKept(null)

        setContent(page.content)
        setSavedContent(page.content)
        lastEdited.current = null
        setDoc((page.doc as JSONContent | null) ?? null)
        setFrontmatter(page.frontmatter)
        revision.current = page.revision
        setSaveState('clean')
        if (!opts.silent && out.canonicalised) {
          toasts.info('Tidied while saving', 'Formatting was normalised to the canonical form.')
        }
        await refresh(on)
      } catch (e) {
        /**
         * Somebody else saved this page while it was open.
         *
         * Two people on one branch used to overwrite each other in silence: both
         * open the page, both save, and the second write replaced the first with
         * content whose author had never seen it. The work was simply gone.
         *
         * The edit is kept — pending and dirty — so nothing is lost by being
         * told. What cannot be done automatically is the merge: their paragraph
         * and yours are both real, and picking one is a decision.
         */
        if (e instanceof RequestFailed && e.code === 'stale_page') {
          setSaveState('conflict')
          // Fetch what is on the draft NOW, so the decision surface can show
          // both versions instead of sending somebody to a second tab.
          try {
            const theirs = await api.page(path, on)
            // What YOURS looks like as text. A block-editor edit lives as a
            // document, and showing the stale markdown beside their fresh text
            // would present a conflict between two versions neither of which
            // is what the person actually wrote.
            const mine =
              pendingText.current ??
              (pendingDoc.current ? (await api.previewDoc(pendingDoc.current)).markdown : content)
            setConflict({ theirs: theirs.content, mine, revision: theirs.revision })
          } catch {
            toasts.warn(
              'Somebody else saved this page',
              'Your changes are still here, unsaved. Open it again in another tab to see theirs.',
              // Sticky: this one asks somebody to go and compare two versions,
              // and a job that disappears after four seconds is a job nobody
              // was really given.
              { sticky: true },
            )
          }
          return
        }
        /**
         * The network, not the server. fetch throws a TypeError when there is
         * nobody to talk to — the edit is safe in this browser (mirrored on
         * every keystroke) and will be retried the moment the connection is
         * back. Saying "failed" here would send somebody hunting for a bug
         * that is actually their hotel Wi-Fi.
         */
        if (e instanceof TypeError || !navigator.onLine) {
          setSaveState('offline')
          return
        }
        /**
         * The session ended while somebody was typing.
         *
         * The words are safe — mirrored on every keystroke, and offered back
         * when the page is next opened — but until this branch existed the
         * screen said "Not saved: not signed in" and stopped there: no gate, no
         * sign-in, nothing to press. The way back was to guess at reloading,
         * which is a thing to guess with unsaved work on the screen.
         *
         * Clearing the session shows the gate, and the kept copy is what makes
         * that safe rather than costly: sign in, open the page, and the browser
         * offers the words back.
         */
        if (e instanceof NotSignedIn) {
          setSaveState('error')
          /**
           * Said on the gate, not in a toast: clearing the session replaces the
           * whole screen with the sign-in, and the toast layer goes with it. A
           * measured mistake — the first version of this toasted, and the words
           * were on screen for one frame.
           */
          setSessionEnded(true)
          setSession(null)
          return
        }
        /**
         * The budget on the content API, spent.
         *
         * The edit is safe in this browser, and the wait is a number the server
         * states — so this schedules the retry rather than waiting for the next
         * keystroke. Somebody who stops typing at the limit would otherwise sit
         * on "not saved" until they touched the page again, which is the one
         * moment they are least likely to.
         */
        const again = retryDelaySeconds(e)
        if (again !== null) {
          setSaveState('error')
          toasts.warn(
            'Saving again shortly',
            because(e, `Nothing was lost — this retries by itself.`),
          )
          window.setTimeout(() => void save({ silent: true }), again * 1000)
          return
        }
        setSaveState('error')
        toasts.error(
          'Not saved — your edit is kept in this browser',
          `${e instanceof Error ? e.message : String(e)}. The next change tries again.`,
        )
      } finally {
        saving.current = false
        // Whatever arrived while this was in flight. A microtask, not a direct
        // call, so the stack unwinds and the refs are settled first.
        if (again.current) {
          again.current = false
          if (pendingDoc.current !== null || pendingText.current !== null) {
            queueMicrotask(() => void save(opts))
          }
        }
      }
    },
    [branch, path, content, refresh], // eslint-disable-line react-hooks/exhaustive-deps
  )

  /**
   * Change some of the page's fields, and reload what that produced.
   *
   * Shared by the Inspector and by the "unreviewed suggestion" badge, which
   * clears §22's marker — two controls for the same file, and a second copy of
   * this would be a second chance to forget the revision line below.
   */
  const patchFrontmatter = useCallback(
    async (patch: FrontmatterPatch) => {
      if (!path) return
      /*
       * A draft, if there is not one — the same silence as the first keystroke
       * in the body.
       *
       * This used to return when there was no branch, which was invisible and
       * therefore worse than a refusal: the title typed at the top of the
       * document simply did not save, and nothing said so. Every other write
       * in this product makes its own draft.
       */
      const on = branch ?? (await startWriting({ silent: true }))
      if (!on) return
      try {
        await api.frontmatter(on, path, patch)
        const page = await api.page(path, on)
        setContent(page.content)
        setSavedContent(page.content)
        lastEdited.current = null
        setDoc((page.doc as JSONContent | null) ?? null)
        setFrontmatter(page.frontmatter)
        // A frontmatter edit rewrites the file, so the body's revision moved
        // too. Without this the next body save is refused as stale against a
        // change this editor made itself.
        revision.current = page.revision
        await refresh(on)
      } catch (e) {
        toasts.error(
          'Could not save that field',
          because(
            e,
            `The page itself is untouched. Change the field again to retry, or reload the page if it keeps refusing.`,
          ),
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branch, path],
  )

  useEffect(() => {
    if (saveState !== 'dirty' || readOnly) return
    const timer = window.setTimeout(() => void save({ silent: true }), AUTOSAVE_MS)
    return () => window.clearTimeout(timer)
  }, [saveState, readOnly, save])

  // The moment the connection is back, whatever is pending goes out.
  useEffect(() => {
    const retry = () => {
      if (pendingDoc.current !== null || pendingText.current !== null) void save({ silent: true })
    }
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [save])

  /**
   * Which surface the person typed in last. The preview used to prefer the
   * block doc whenever one existed — so an edit made in Markdown mode
   * previewed as the page WITHOUT the edit, silently. A preview that shows
   * yesterday's text under today's cursor is worse than none.
   */
  const lastEdited = useRef<'doc' | 'text' | null>(null)

  /**
   * Every unsaved edit, mirrored to this browser as it is typed.
   *
   * The server copy is the real one; this is the seatbelt. Wi-Fi drops, the
   * laptop sleeps, the tab is closed with a save mid-flight — before this,
   * whatever had not reached the server was simply gone. Now it is in
   * localStorage under the page it belongs to, cleared the moment a save
   * lands, and offered back — never silently applied — when the page is next
   * opened. Storage can be full or blocked; failing to mirror must never
   * break typing, hence the try/catch on every touch.
   */
  const keptKey = useCallback(
    (forPath: string) => `staleless:kept:${workspace?.slug ?? ''}:${branch ?? ''}:${forPath}`,
    [workspace?.slug, branch],
  )
  const keepCopy = (forPath: string, body: { text?: string; doc?: JSONContent }) => {
    try {
      localStorage.setItem(keptKey(forPath), JSON.stringify({ ...body, at: Date.now() }))
    } catch {
      /* a full or blocked store must never break typing */
    }
  }
  const dropCopy = useCallback(
    (forPath: string) => {
      try {
        localStorage.removeItem(keptKey(forPath))
      } catch {
        /* ditto */
      }
    },
    [keptKey],
  )
  const [kept, setKept] = useState<{ text?: string; doc?: JSONContent; at: number } | null>(null)
  /** Their version, fetched when a save is refused as stale. */
  /**
   * A selection handed from the editor to the comments panel, waiting for its
   * first comment. Held here rather than in the panel because the SELECTION
   * lives in the editor and the panel is a sibling — the shell is the only
   * place both can see.
   */
  const [pendingQuote, setPendingQuote] = useState<{
    quote: string
    prefix: string
    suffix: string
  } | null>(null)
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({})

  const [conflict, setConflict] = useState<{
    theirs: string
    mine: string
    revision: string
  } | null>(null)

  /**
   * A commit refused because the branch moved — a different conflict from the
   * one above, and kept apart from it on purpose.
   *
   * `conflict` is one page saved twice inside the product. This is somebody
   * pushing to the customer's own repository while a draft was open, which is
   * a decision about a set of files rather than about the page on screen. The
   * commit MESSAGE is held with it: the writer already typed one, and asking
   * for it again after a refusal that cost them nothing would be a second
   * punishment for somebody else's push.
   */
  const [moved, setMoved] = useState<{ report: ConflictReport; message: string } | null>(null)

  const markDirtyDoc = (next: JSONContent) => {
    pendingDoc.current = next
    lastEdited.current = 'doc'
    if (path) keepCopy(path, { doc: next })
    setDoc(next)
    setSaveState('dirty')
  }
  const markDirtyText = (next: string) => {
    pendingText.current = next
    lastEdited.current = 'text'
    if (path) keepCopy(path, { text: next })
    setContent(next)
    setSaveState('dirty')
  }

  // ---------------------------------------------------------------- pages

  /**
   * Open a page.
   *
   * `on` names the draft to read it from, and defaults to the one open. It is
   * a parameter because the caller that has just CREATED a draft cannot rely
   * on the default: `setBranch` does not change this closure, so every "make a
   * draft, then open a page on it" path read the live site instead and showed
   * "that page does not exist" for a page it had written a moment earlier.
   * Found by starting from a template in a browser.
   */
  const openPage = useCallback(
    async (next: string, on: string | null = branch) => {
      if (saveState === 'dirty') await save({ silent: true })
      /**
       * The page being LEFT, and whether anything was typed while this open
       * was in flight.
       *
       * An open is two round trips on a busy machine, and the editor stays on
       * screen and editable for the whole of it — so somebody who clicks a row
       * and starts typing straight away is typing into the document that is
       * still there. Everything below used to run regardless: the pending
       * buffer was cleared, the state was set to `clean`, and the words stayed
       * on screen with nothing left that would ever save them. The page said
       * "Saved". Nothing was.
       *
       * Found by the journey gate, which types the moment the row is clicked —
       * which is what a person in a hurry does.
       */
      const leaving = path
      try {
        const page = await api.page(next, on ?? undefined)
        /**
         * Anything pending against the page being LEFT, put away before the
         * editor moves off it.
         *
         * `setPath` is below this line, so the closure's `path` and
         * `revision.current` are both still the page those words were typed
         * into: this is the same save the top of this function makes, for the
         * edit that arrived a moment too late to be caught by it.
         *
         * Only when the page is actually CHANGING. Re-opening the page that is
         * already open is the ordinary case, and the editor leaves a pending
         * echo of its own document behind every open — which is what the
         * unconditional clearing below is really for. Saving on that path
         * would write the document back over itself on every click, and
         * returning early on it robs the "we kept your unsaved copy" bar of
         * the moment it is offered.
         */
        if (next !== leaving && (pendingDoc.current !== null || pendingText.current !== null)) {
          await save({ silent: true })
        }
        setPath(next)
        setContent(page.content)
        setSavedContent(page.content)
        lastEdited.current = null
        setDoc((page.doc as JSONContent | null) ?? null)
        setFrontmatter(page.frontmatter)
        setHtml(page.html ?? '')
        // A copy kept from an offline edit or a closed tab, offered back —
        // never applied silently: the server may have moved on since.
        try {
          const raw = localStorage.getItem(
            `staleless:kept:${workspace?.slug ?? ''}:${on ?? ''}:${next}`,
          )
          const held = raw
            ? (JSON.parse(raw) as { text?: string; doc?: JSONContent; at: number })
            : null
          setKept(held && held.text !== page.content ? held : null)
          if (held && held.text === page.content) {
            // Already on the server — a stale seatbelt is just clutter.
            localStorage.removeItem(`staleless:kept:${workspace?.slug ?? ''}:${on ?? ''}:${next}`)
          }
        } catch {
          setKept(null)
        }
        // The revision this editor is now working against. Without this, the
        // first save after opening a page sends the PREVIOUS page's revision.
        revision.current = page.revision
        pendingDoc.current = null
        pendingText.current = null
        setSaveState('clean')
        /*
         * A site file is YAML, not a document, so it only has the text
         * surface. This named `nav.yaml` alone, so the other five opened in
         * the BLOCK editor — and the block editor's save serialises what it
         * holds back into Markdown. Opening `search.yaml` through the door the
         * Search screen offers and typing one character stripped its
         * indentation and escaped its brackets, which ends every alias,
         * synonym, pin and weight in it without telling anybody.
         */
        setView(isSiteFile(next) ? 'markdown' : 'blocks')
      } catch (e) {
        toasts.error(
          'Could not open that page',
          because(e, `The page is still there. Pick it again from the tree, or reload the page.`),
        )
      }
    },
    [branch, saveState, save], // eslint-disable-line react-hooks/exhaustive-deps
  )

  /**
   * Keep the address true. Pushing is navigation's job (`setPlace`); this
   * only REPLACES, so the page part of a Pages address follows the open page
   * without burying Back under every page somebody read.
   */
  useEffect(() => {
    if (!state) return
    const wanted = hashFor(place, place === 'pages' ? path : null)
    if (window.location.hash !== wanted) {
      window.history.replaceState(window.history.state, '', wanted)
    }
  }, [state, place, path])

  /**
   * The page the address asked for, opened once the tree exists. Guarded off
   * the first run: the sequence replaces the whole shell, and opening a page
   * under it would race the template writes.
   */
  useEffect(() => {
    if (!state || onboarding === undefined || onboarding?.shouldOnboard) return
    const id = wantedPage.current
    if (!id) return
    wantedPage.current = null
    if (place !== 'pages') return
    void openPage(`pages/${id}.md`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, onboarding])

  /**
   * Open a file the sidebar offers, creating the ones that describe the site.
   *
   * `nav.yaml` always exists — every project is seeded with one. `glossary.yaml`
   * usually does not, and a button that answers "that page does not exist" is
   * a door into a wall. So the first press on a draft writes the starter file,
   * which is comments and an example and no terms: it parses to an empty
   * glossary, so nothing is warned about until somebody means it.
   */
  const openFile = useCallback(
    async (next: string) => {
      /**
       * `style.yaml` is the same kind of file as the glossary and gets the same
       * treatment: usually absent, written from a starter on the first press.
       */
      const startable: Record<string, string> = {
        'glossary.yaml': GLOSSARY_STARTER,
        'style.yaml': STYLE_STARTER,
      }
      const missing =
        startable[next] !== undefined && !state?.pages.some((page) => page.path === next)
          ? await api.page(next, branch ?? undefined).then(
              () => false,
              () => true,
            )
          : false
      let on = branch
      if (missing) {
        /*
         * A draft, if there is not one already.
         *
         * This used to warn "start a draft, and this will write one for you" —
         * which is a door telling somebody to go and open a different door
         * first. Every write in this product needs a draft; the product knows
         * how to start one, and the press is unambiguous.
         */
        if (!on) {
          on = await startWriting()
          if (!on) {
            toasts.warn(
              next === 'style.yaml' ? 'There is no style guide yet' : 'There is no glossary yet',
              'Start a draft, and this will write one for you to fill in.',
            )
            return
          }
        }
        try {
          await api.write(on, next, startable[next]!)
          await refresh(on)
        } catch (e) {
          toasts.error(
            next === 'style.yaml' ? 'Could not start a style guide' : 'Could not start a glossary',
            because(
              e,
              `Nothing was written. Try again — if it keeps refusing, you may have view access only.`,
            ),
          )
          return
        }
      }
      /*
       * And GO there, which this did not do.
       *
       * Two of the four callers are screens of their own — the Search screen's
       * "Open search.yaml to write it by hand", and the Words screen's pair —
       * and the command palette can be opened from anywhere. All three opened
       * the file behind a screen that does not show one, so the button did
       * nothing a person could see. The sidebar's own caller set the place
       * itself, which is why the doors that were added later did not: the
       * knowledge was in a call site rather than in the thing being called.
       *
       * After the early returns above, never before them: somebody who is told
       * "there is no glossary yet" should be told it where they are standing.
       */
      setPlace('pages')
      await openPage(next, on ?? undefined)
    },
    [branch, state, openPage], // eslint-disable-line react-hooks/exhaustive-deps
  )

  /**
   * Apply a drag, or a keyboard move, from the sidebar.
   *
   * Reads nav.yaml fresh rather than trusting `state.nav`, for the same reason
   * every other save on this page carries a revision: the sidebar can be open
   * for a while before a drop, and nav.yaml is one file everybody's edits share,
   * so a stale copy would silently discard whatever else changed it meanwhile.
   */
  const reorderNav = useCallback(
    async (from: NavPath, to: DropTarget) => {
      const on = await somewhereToWrite()
      if (!on) return
      try {
        const current = await api.page(NAV_PATH, on)
        const outcome = moveNavItem(parseNav(current.content), from, to)
        if (!outcome.ok) {
          // `cycle` and `no-op` are refused silently: the sidebar already
          // declines to show a drop indicator for either, so reaching this
          // with one of them means nothing was dropped, not that a drop failed.
          if (outcome.reason === 'not-found') {
            toasts.warn('The navigation changed underneath that', 'Reloading — try the move again.')
            await refresh(on)
          }
          return
        }
        // `moveNavItem` only rearranges items the schema already accepted for
        // this very file, so it always produces another file the schema
        // accepts.
        // The file it replaces, so the writer's own notes in it survive a drag.
        const text = serializeNav(outcome.nav as Nav, current.content)
        const written = await api.write(on, NAV_PATH, text, current.revision)
        // nav.yaml, open in the editor itself, must not go on to autosave its
        // own now-outdated in-memory copy over what was just written here.
        if (path === NAV_PATH) {
          setContent(text)
          setSavedContent(text)
          revision.current = written.revision
          pendingText.current = null
          setSaveState('clean')
        }
        await refresh(on)
      } catch (e) {
        if (e instanceof RequestFailed && e.code === 'stale_page') {
          toasts.warn('Somebody else changed the navigation', 'Reloading — try the move again.')
          await refresh(on)
          return
        }
        toasts.error(
          'Could not update the navigation',
          because(
            e,
            `The tree has been put back as it was, so nothing is half-moved. Try the move again.`,
          ),
        )
      }
    },
    [branch, path, refresh], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const loadDiff = useCallback(async () => {
    if (!branch) return
    try {
      const r = await api.diff(branch)
      setDiff(r.diff)
    } catch {
      setDiff('')
    }
  }, [branch])

  // Preview renders server-side, through the same code as the published page.
  const previewTimer = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (view !== 'preview' || !path) return
    window.clearTimeout(previewTimer.current)
    previewTimer.current = window.setTimeout(() => {
      const useDoc = doc && !isSiteFile(path) && lastEdited.current !== 'text'
      // The draft's own variables resolve in the preview, so it shows what
      // publishing this draft would show.
      const request = useDoc
        ? api.previewDoc(doc, branch ?? undefined)
        : api.preview(content, branch ?? undefined)
      void request.then((r) => setHtml(r.html)).catch(() => undefined)
    }, 220)
    return () => window.clearTimeout(previewTimer.current)
  }, [view, content, doc, path])

  useEffect(() => {
    if (view === 'diff') void loadDiff()
  }, [view, loadDiff])

  /**
   * Draw any diagrams the preview HTML contains — the same loop the published
   * site runs, from @staleless/markdown, so the preview shows what will ship.
   * Mermaid itself loads lazily and only when a page actually has one.
   */
  const previewRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const rootEl = previewRef.current
    if (view !== 'preview' || !rootEl || !rootEl.querySelector('.sls-mermaid')) return
    let gone = false
    void import('mermaid').then(({ default: mermaid }) => {
      if (!gone) void drawMermaidDiagrams(rootEl, mermaid, 'default')
    })
    return () => {
      gone = true
    }
  }, [view, html])

  // ---------------------------------------------------------------- keys

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey
      const typing =
        e.target instanceof HTMLElement &&
        (e.target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName))

      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        e.stopPropagation()
        setPaletteOpen(true)
      } else if (meta && e.key.toLowerCase() === 's') {
        e.preventDefault()
        void save()
      } else if (meta && e.shiftKey && e.key.toLowerCase() === 'r') {
        e.preventDefault()
        openReview()
      } else if (meta && e.key === '\\') {
        e.preventDefault()
        // Inline rather than through `setFolded`: this listener re-registers
        // on `save`, and reading `folded` here would make it re-register on
        // every fold as well.
        setFoldedState((v) => {
          try {
            localStorage.setItem('staleless:sidenav', v ? 'open' : 'folded')
          } catch {
            // Forgetting between visits is the only cost.
          }
          return !v
        })
      } else if (e.key === '?' && !typing) {
        e.preventDefault()
        setHelpOpen(true)
      }
    }
    /*
     * CAPTURE, so the shell's own shortcuts win.
     *
     * The editor binds ⌘K itself — it is the link shortcut in every editor
     * anybody has used — so with the cursor in the document, the one
     * shortcut this product advertises in its sidebar reached nothing. A
     * shortcut the interface prints on screen has to work wherever the
     * keyboard happens to be, and the shell is the outer thing: it hears the
     * key first and stops it there.
     */
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [save]) // eslint-disable-line react-hooks/exhaustive-deps

  const openReview = useCallback(() => {
    trackProductEvent('review.opened', { surface: 'review' })
    void loadDiff()
    setReviewOpen(true)
  }, [loadDiff])

  /**
   * Upload a file for the editor.
   *
   * Lives here because this is where the API client and the toasts are. It
   * reports its own failures — a refusal names the reason, because "that file is
   * 14 MB, the limit is 10" is actionable and "upload failed" is not — and
   * returns null so the caller has nothing to decide.
   */
  const upload = useCallback(
    async (file: File) => {
      try {
        const result = await api.uploadAsset(file)
        if (result.deduplicated) {
          // Worth saying once. Otherwise somebody uploads the same screenshot
          // twice, sees one entry in the library, and wonders what went wrong.
          toasts.info('That image was already here', 'Reusing the copy already uploaded.')
        }
        return { url: result.url, altText: result.altText }
      } catch (e) {
        toasts.error(
          'Could not add that image',
          because(
            e,
            `Nothing was added to the page. Try a smaller file, or a PNG, JPEG, SVG or GIF.`,
          ),
        )
        return null
      }
    },
    [toasts],
  )

  // ---------------------------------------------------------------- palette

  const commands: Command[] = useMemo(() => {
    if (!state) return []
    /**
     * Pages by their TITLE, and placed by their section.
     *
     * The palette listed slugs — `guides/install` — with the file path as the
     * hint, which is the storage layer wearing the product's most-used
     * control: a writer looking for "Installing on Windows" had to know what
     * it was called on disk. The slug stays as a keyword, so anybody who does
     * know it still finds the page by typing it.
     */
    const sectionOf = new Map<string, string>()
    for (const section of state.nav.sections) {
      const walk = (items: typeof section.items, trail: string): void => {
        for (const item of items) {
          if (item.page) sectionOf.set(item.page, trail)
          if (item.items) walk(item.items, trail)
        }
      }
      walk(section.items, section.label)
    }
    const pages: Command[] = state.pages.map((p) => ({
      id: `page:${p.path}`,
      label: p.id === 'index' ? 'Home' : (p.title ?? p.id),
      hint: sectionOf.get(p.id) ?? (p.id === 'index' ? 'The home page' : 'Not in the navigation'),
      group: 'Pages',
      icon: 'page',
      keywords: `${p.id} ${p.path}`,
      run: () => void openPage(p.path),
    }))

    const actions: Command[] = [
      {
        id: 'a:nav',
        label: 'Edit the navigation',
        hint: 'nav.yaml — ordering for the whole site',
        group: 'Actions',
        icon: 'nav',
        run: () => void openPage(NAV_PATH),
      },
      {
        /**
         * The vocabulary, beside the ordering: both are files about the whole
         * site, and the palette is where somebody looks for a thing they
         * cannot see. Opens the starter on a draft when there is no glossary
         * yet, exactly as the tree's own button does.
         */
        id: 'a:words',
        label: 'Edit the words this documentation uses',
        hint: 'glossary.yaml — the words to use, and the ones to replace',
        group: 'Actions',
        icon: 'quote',
        run: () => void openFile('glossary.yaml'),
      },
      /**
       * The templates, once the checklist has stopped offering them.
       *
       * The checklist hides the step as soon as there is more than the starter
       * page — a finished step that stays is a nag — and that left the
       * capability with no door at all on any established site. The palette is
       * where somebody looks for a thing they cannot see, and four entries
       * rather than one prompt because it is a four-way question and a typo in
       * a prompt is an error message.
       */
      ...(['product', 'api', 'help', 'handbook'] as UseCase[]).map((kind) => ({
        id: `a:template:${kind}`,
        label: `Start from a template: ${USE_CASE_LABELS[kind]}`,
        hint: 'Writes the pages you do not have yet, on a draft',
        group: 'Actions',
        icon: 'pages' as Command['icon'],
        /*
         * A viewer may not write; anybody else may, even with no draft
         * selected. `readOnly` would be wrong here — it is true when there is
         * no draft, and this is the one action that MAKES one, so it would
         * disable itself exactly when somebody needs it.
         */
        disabled: role === 'viewer',
        run: () => void startFromTemplate(kind),
      })),
      {
        id: 'a:new',
        label: 'New page',
        group: 'Actions',
        icon: 'plus',
        disabled: readOnly,
        run: () => askForAPage(),
      },
      {
        id: 'a:branch',
        label: 'New branch',
        hint: 'Nothing is editable until you have one',
        group: 'Actions',
        icon: 'branch',
        run: () => setMaking('draft'),
      },
      {
        id: 'a:review',
        label: 'Draft workflow',
        hint: `${state.staged.length} waiting`,
        group: 'Actions',
        icon: 'diff',
        disabled: readOnly,
        run: openReview,
      },
      ...(['light', 'dark', 'system'] as Theme[]).map((t) => ({
        id: `t:${t}`,
        label: `Theme: ${t}`,
        group: 'Appearance',
        icon: (t === 'light' ? 'sun' : t === 'dark' ? 'moon' : 'monitor') as Command['icon'],
        run: () => setTheme(t),
      })),
      {
        id: 'a:help',
        label: 'Keyboard shortcuts',
        group: 'Appearance',
        icon: 'keyboard',
        run: () => setHelpOpen(true),
      },
      {
        id: 'a:developers',
        label: 'Developers: MCP & CLI',
        hint: 'Agent access, tokens and command-line checks',
        group: 'Go to',
        icon: 'code',
        run: () => setPlace('developers'),
      },
      {
        id: 'a:help-docs',
        label: 'Help & docs',
        hint: 'Client guides for using Staleless',
        group: 'Go to',
        icon: 'callout',
        run: () => setPlace('help'),
      },
    ]

    return [...pages, ...actions]
  }, [state, readOnly, openPage, openFile, openReview]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- render

  /**
   * First, above everything, because everything else is a consequence of it.
   *
   * A deleted workspace answers 403 on the next poll and a deleted account
   * answers 401; either would take this screen away before it had been read,
   * and the terms it carries are stated nowhere else in the product.
   */
  if (deleted) {
    return <Deleted what={deleted} onLeave={() => window.location.assign('/')} />
  }

  // Undefined means the first /auth/me is still in flight. Showing the gate
  // here would flash a sign-in form at somebody who is already signed in.
  if (session === undefined) {
    return (
      <div className="gate">
        <span className="gate__mark">
          <Icon name="logo" size={26} />
        </span>
        <h1>Staleless</h1>
      </div>
    )
  }

  if (session === null) {
    return (
      <Gate
        movedTo={movedTo}
        sessionEnded={sessionEnded}
        invitation={invitation}
        onSignedIn={(next, signedUp, spent) => {
          setSessionEnded(false)
          setWorkspace(adoptWorkspace(next))
          /**
           * A recovery code was spent to get in, said where everything else
           * that just happened is said.
           *
           * The person who has just used one is exactly the person who needs
           * to know how many are left, and this is the one moment they are
           * looking. The last one is a different sentence and a different
           * weight: until two-step sign-in is set up again, a lost
           * authenticator locks this account out — so it stays until it is
           * dismissed, and it offers the screen that fixes it rather than
           * naming it and leaving somebody to go and find it.
           */
          if (next.usedRecoveryCode && typeof next.recoveryCodesLeft === 'number') {
            const left = next.recoveryCodesLeft
            if (left === 0) {
              toasts.warn(
                'That was your last recovery code',
                'Until you set two-step sign-in up again, a lost authenticator locks you out of this account.',
                {
                  sticky: true,
                  action: { label: 'Set it up again', onClick: () => setPlace('settings') },
                },
              )
            } else {
              toasts.info(
                'Recovery code used',
                `${left} left — each one works once, and they are the way back in without your authenticator.`,
              )
            }
          }
          // Only when a workspace was CREATED. Somebody who signed up into an
          // invitation joined one that has existed for months, and telling
          // them it is being set up is the false sentence this flag exists to
          // avoid.
          if (signedUp && !spent) setJustSignedUp(true)
          // Signing up WITH the invitation spends it in the same transaction,
          // so the effect below must not try again — a second attempt finds it
          // used and shows a red message about a link that worked.
          if (spent) setInvitation(null)
          setSession(next)
        }}
      />
    )
  }

  /**
   * A workspace that requires two-step sign-in, met by somebody without one.
   *
   * Before the boot screen deliberately: `/api/state` is refused for this
   * session, so waiting for it would show "cannot reach the API" about a
   * server that answered correctly and immediately. The session is real — it
   * had to be, because enrolling needs one — and the server refuses everything
   * under `/api/` until an authenticator is confirmed.
   *
   * Enrolment is the gate, never rejection. Nobody is told "no" about their
   * own documentation; they are told what to add, and by whom it was asked.
   */
  if (session.enrolTwoFactor) {
    return (
      <EnrolTwoFactor
        workspace={session.enrolTwoFactor.name}
        onEnrolled={() => {
          /*
           * Re-read rather than assumed. The server clears the mark on every
           * session this person holds when the code is confirmed, and asking
           * it is what makes this screen leave for the right reason.
           */
          void (async () => {
            const next = await api.me().catch(() => null)
            if (next) setSession(next)
            await refresh(null)
            await loadOnboarding()
          })()
        }}
        onSignOut={() => void signOutEverywhere()}
      />
    )
  }

  // Held until the first-run answer arrives as well as the state: flashing
  // Home at somebody the sequence is about to replace reads as the app
  // changing its mind on its first breath.
  if (!state || onboarding === undefined)
    return (
      <Boot
        error={bootError}
        justSignedUp={justSignedUp}
        onRetry={() => {
          void refresh(null)
          void loadOnboarding()
        }}
        onReset={async () => {
          // Signing out revokes the session server-side. Clearing local state
          // alone would leave a live session behind on a shared machine.
          await api.signOut().catch(() => {})
          setSession(null)
        }}
        onCreateWorkspace={async (name) => {
          const made = await api.createWorkspace(name)
          setSession((current) => (current ? { ...current, workspaces: made.workspaces } : current))
          setBootError(null)
          setWorkspaceSlug(made.workspace.slug)
          setProjectState(activeProject())
          setWorkspace(made.workspace)
          // A brand-new workspace provisions a repository, which is exactly
          // what this flag is for.
          setJustSignedUp(true)
          await refresh(null)
        }}
      />
    )

  if (onboarding?.shouldOnboard) {
    /** The run is over on screen the moment a landing is chosen; the server
     *  was told by the component, and disagreeing with it here would show the
     *  sequence twice in one visit. A functional update, because the
     *  component reports the server's fresher status through `onStatus`
     *  first — spreading this render's stale copy would erase it. */
    const endRun = () =>
      setOnboarding((current) => (current ? { ...current, shouldOnboard: false } : current))
    return (
      <Onboarding
        workspaceName={workspace?.name ?? 'Your workspace'}
        mayInvite={onboarding.mayInvite}
        onStatus={setOnboarding}
        onWrite={() => {
          endRun()
          setPlace('pages')
          // The editor, on a draft, with the starter page open — the writing
          // surface itself, not a screen about it. Both steps tolerate failure:
          // landing in Pages with the tree showing is still the right room.
          void (async () => {
            // Named, not defaulted: the draft was created a line ago, and this
            // closure still holds the branch from before it existed.
            const started = await startWriting()
            await openPage('pages/index.md', started).catch(() => undefined)
          })()
        }}
        onTemplate={(useCase) => void startFromTemplate(useCase, endRun)}
        onImport={() => {
          endRun()
          setPlace('import')
        }}
        onUpgrade={() => {
          endRun()
          setSettingsAt('org/plan')
          setPlace('settings')
        }}
        onSkip={() => {
          endRun()
          setPlace('home')
        }}
      />
    )
  }

  const pageProblems = path ? state.lint.problems.filter((p) => p.file === path) : []
  const branchInfo = state.branches.find((b) => b.name === branch)
  const staged = state.staged.length
  const siteName = projects.find((p) => p.slug === (project ?? projects[0]?.slug))?.name ?? null
  /**
   * Every place a new page could go, as a person would read it: "Guides", or
   * "Guides › Getting started". Built from the tree on screen, so the picker
   * and the tree can never disagree about what exists.
   */
  /**
   * What a row in the tree can do to itself.
   *
   * Every one of these existed somewhere else already — rename and delete in
   * the inspector, the address on the published site in the top bar — and
   * "somewhere else" is the problem: the tree is where somebody is looking
   * when they decide a page is misnamed or unwanted. Absent for a viewer and
   * on the read-only live site, so nothing here is offered where it would be
   * refused.
   */
  const rowActions = readOnly
    ? undefined
    : {
        newChild: (under: NavPath) => {
          // The dialog asks the rest; this only says which place is meant.
          setPlaceUnder(under)
          askForAPage()
        },
        rename: (at: NavPath, label: string) => void renameInNav(at, label),
        duplicate: (path: string) => void duplicatePage(path),
        copyLink: (path: string) => {
          const address = siteAddress ? publishedUrl(siteAddress, path) : null
          if (!address) {
            toasts.warn('There is no address yet', 'Publish the site, and its pages get one.')
            return
          }
          void navigator.clipboard
            .writeText(address)
            .then(() => toasts.info('Link copied', address))
            // Refusing the clipboard is a browser decision, not a failure of
            // the product — so the address is said instead of copied.
            .catch(() => toasts.info('Here is the link', address))
        },
        openOnSite: (path: string) => {
          if (!siteAddress) {
            toasts.warn('There is no published site yet', 'Publish, and this opens the page.')
            return
          }
          window.open(publishedUrl(siteAddress, path), '_blank', 'noopener')
        },
        trash: (path: string) => void trashPage(path),
        newSection: () => setMaking('section'),
        renameSection: (index: number, label: string) => void renameSectionInNav(index, label),
        removeSection: (index: number) => void removeSectionFromNav(index),
      }

  const places = navPlaces(state.nav, (id) => state.pages.find((p) => p.id === id)?.title ?? null)
  /**
   * Which of those places the dialog should open on, when the question came
   * from a row's "+" — the row itself. Falls back to the first, which is the
   * first section, for the button at the foot of the tree.
   */
  const placeFromRow = placeUnder
    ? places.findIndex((p) => p.target.containerPath.join('.') === placeUnder.join('.'))
    : 0
  /**
   * The rest of the breadcrumb, after the site: the place, or — in Pages —
   * the open page's own address with its title as the leaf. From state, not
   * from the hash: the address follows this, never the other way round.
   */
  const crumbs = (() => {
    if (place !== 'pages') {
      const names = PLACE_NAMES
      return [names[place]]
    }
    if (!path) return ['Pages']
    if (isSiteFile(path)) return [SITE_FILE_CRUMBS[path as SiteFile]]
    const parts = path
      .replace(/^pages\//, '')
      .replace(/\.md$/, '')
      .split('/')
    const title = state.pages.find((p) => p.path === path)?.title
    if (title && parts.length > 0) parts[parts.length - 1] = title
    return parts
  })()

  return (
    <div className="app">
      {/* The drawer's own backdrop, phone width only — the CSS hides it wider. */}
      {navOpen && <div className="sidenav-scrim" onMouseDown={() => setNavOpen(false)} />}
      <SideNav
        place={place}
        onPlace={setPlace}
        folded={folded}
        onFold={setFolded}
        mobileOpen={navOpen}
        onMobileClose={() => setNavOpen(false)}
        inboxCount={inboxCount}
        onInbox={openInbox}
        onSearch={() => setPaletteOpen(true)}
        onPeople={openPeople}
        bell={
          /* People first, machines second: what a person is waiting on you
             for sits beside the switcher, where the eye starts. */
          <Notifications
            onOpenPage={(p, b) => {
              setPlace('pages')
              if (b && b !== branch) {
                setBranch(b)
                /**
                 * On the branch the notification named, not on the branch this
                 * closure remembers — see the comment on `openPage`.
                 */
                void refresh(b).then(() => openPage(p, b))
              } else {
                void openPage(p)
              }
            }}
            onOpenSettings={(at) => {
              if (at) setSettingsAt(at)
              setPlace('settings')
            }}
            onOpenDraft={(b) => {
              setPlace('pages')
              setBranch(b)
              void refresh(b)
              openReview()
            }}
          />
        }
        state={state}
        selected={path}
        onSelectFile={(p) => {
          setPlace('pages')
          void openFile(p)
        }}
        onNewPage={() => askForAPage()}
        onReorder={(from, to) => void reorderNav(from, to)}
        onTrash={() => setTrashOpen(true)}
        readOnly={readOnly}
        {...(rowActions ? { rowActions } : {})}
        workspaceName={workspace?.name ?? 'Staleless'}
        siteName={siteName}
        workspaces={session?.workspaces ?? []}
        currentWorkspace={workspace?.slug ?? null}
        onSwitchWorkspace={switchWorkspace}
        projects={projects}
        project={project ?? projects[0]?.slug ?? null}
        onSwitchProject={switchProject}
        mayManageSites={workspaceRole === 'owner' || workspaceRole === 'admin'}
        mayAddSite={mayAddSite}
        onNewSite={() => setMaking('site')}
        onUpgrade={() => {
          setSettingsAt('org/plan')
          setPlace('settings')
        }}
        onInvite={openPeople}
        theme={theme}
        onTheme={setTheme}
        onSignOut={() => void signOutEverywhere()}
      />

      <div className="appmain">
        {/* Grouped with the top bar rather than a sibling of it: `.appmain` is a
            two-row grid, header `auto` above content `1fr`, and a banner that
            came and went as a THIRD grid child would leave the content row
            fighting the banner for which one gets `1fr`. Folding it into the
            same `auto` row keeps that math untouched whether or not it renders. */}
        <div className="topstack">
          <header className="top">
            {/* Phone width only — the CSS hides it wider. It opens the
                sidebar drawer, which is the whole navigation at 390px. */}
            <button
              className="btn btn--ghost btn--icon btn--sm top__menu"
              aria-label="Menu"
              onClick={() => setNavOpen((v) => !v)}
            >
              <Icon name="nav" size={14} />
            </button>

            {/* Where you are: the site, then the place — or the open page. */}
            <nav className="crumbs top__crumbs" aria-label="Where you are">
              <span className="truncate">{siteName ?? workspace?.name ?? 'Staleless'}</span>
              {crumbs.map((part, i, all) => (
                <span key={i} className={i === all.length - 1 ? 'crumbs__leaf' : undefined}>
                  <span className="crumbs__sep"> › </span>
                  {part}
                </span>
              ))}
            </nav>

            {/*
              WHICH DRAFT, said where somebody is already reading where they
              are.

              The model is that a draft is made on the first keystroke and
              named after the person and the day, so nobody has to answer a
              branching question before typing — and nothing on screen said so,
              so the top bar read as "Saved · View site · Publish" and a writer
              reasonably concluded they were publishing to the live site from
              nowhere. Reported by somebody using the product: "i don't see what
              branch I am on? I kind of able to publish to main directly?"

              It is a BUTTON rather than a label because the question it raises
              — what is on this draft — has an answer already built, and the
              review drawer is it. The words are `draftLabel`, shared with the
              picker in that drawer, so the two surfaces cannot name one draft
              two ways.
            */}
            <button
              className="top__draft"
              data-draft-chip
              onClick={openReview}
              data-tip={draftTip(branch, staged)}
            >
              <Icon name="branch" size={12} />
              <span className="top__draft__name truncate">{draftLabel(branch, branchInfo)}</span>
              <span className="top__draft__kind">{draftKind(branch)}</span>
            </button>

            <span className="top__spacer" />

            {/* The same fact as the button's own count, said as a state: work
                is waiting. Both open the review, which is where it waits. */}
            {staged > 0 && (
              <button
                className="top__changes"
                onClick={openReview}
                data-tip="Open the review — what these changes are"
              >
                {staged} change{staged === 1 ? '' : 's'} to commit
              </button>
            )}

            <SaveBadge state={saveState} readOnly={readOnly} viewer={role === 'viewer'} />

            {siteAddress && (
              <a
                className="btn btn--ghost btn--sm"
                href={publishedUrl(siteAddress, path)}
                target="_blank"
                rel="noreferrer"
                data-tip="Open the published site — what readers actually see"
              >
                <Icon name="eye" size={13} />
                View site
              </a>
            )}

            <button
              className="btn btn--ghost btn--icon btn--sm"
              onClick={openHistory}
              data-tip="What has been published, and how to take it back"
            >
              <Icon name="history" size={14} label="Published" />
            </button>

            {/* The Shortcuts sheet, until the Help panel exists. A door that
                answers "how do I…" belongs on the chrome, not on a screen. */}
            <button
              className="btn btn--ghost btn--icon btn--sm"
              onClick={() => setHelpPanelOpen(true)}
              data-tip="Help — what this screen is for"
            >
              <span className="top__help" aria-hidden>
                ?
              </span>
              <span className="sr-only">Help</span>
            </button>

            {/*
              Filled only when there is something waiting for a decision.

              This is the one control that lives in the CHROME rather than on a
              screen, so a permanent filled accent here would mean every screen
              opens with its primary action already spoken for. Reserving the
              fill for staged work or an unpublished main makes it say
              something: the accent appears exactly while work is waiting.
              BRAND.md 13, one primary per screen.

              Never disabled: the drawer it opens now holds the draft picker,
              which is exactly the control somebody on the read-only live site
              needs next.
            */}
            <button
              className={`btn btn--sm ${staged > 0 || (!branch && state.release.ahead) ? 'btn--primary' : 'btn--ghost'}`}
              data-release-pending={!branch && state.release.ahead ? '' : undefined}
              onClick={() => {
                if (!branch && state.release.ahead) void publishMain()
                else {
                  trackProductEvent('publish.opened', { surface: 'publish' })
                  setPublishOpen(true)
                }
              }}
              data-tip={
                branch
                  ? 'Review and commit the changes on this draft'
                  : state.release.ahead
                    ? 'Deploy the merged changes currently on main'
                    : 'Open the draft workflow'
              }
            >
              <Icon name="eye" size={13} />
              {branch
                ? staged > 0
                  ? `Commit ${staged} change${staged === 1 ? '' : 's'}`
                  : 'Review draft'
                : state.release.ahead
                  ? 'Publish main'
                  : 'Drafts'}
            </button>
          </header>

          {session?.user && !session.user.emailVerifiedAt && !verifyDismissed && (
            <VerifyBanner onDismiss={() => setVerifyDismissed(true)} />
          )}
        </div>

        {place === 'home' && (
          <Home
            state={state}
            openFindings={openFindings}
            team={people.length > 0 ? people.length : null}
            siteName={siteName}
            useCase={onboarding?.useCase ?? null}
            onWrite={() => setPlace('pages')}
            onOpenPage={(p) => {
              setPlace('pages')
              void openPage(p)
            }}
            onTemplate={(useCase) => void startFromTemplate(useCase ?? onboarding?.useCase ?? null)}
            onImport={() => setPlace('import')}
            onInbox={openInbox}
            onPulse={() => setPlace('pulse')}
            onReview={openReview}
            onPeople={openPeople}
            onSettings={() => setPlace('settings')}
            onTour={() => {
              setPlace('pages')
              // After the screen it is about has arrived.
              window.setTimeout(() => setTourRunning('pages'), 500)
            }}
          />
        )}

        {place === 'pulse' && (
          <PulseScreen
            state={state}
            branch={branch}
            onOpenPage={(p) => {
              setPlace('pages')
              void openPage(p)
            }}
            onWrite={() => setPlace('pages')}
            onAnalytics={() => setPlace('analytics')}
          />
        )}

        {place === 'analytics' && (
          <AnalyticsScreen
            state={state}
            branch={branch}
            readOnly={readOnly}
            onStaged={() => void refresh()}
            onOpenPage={(p) => {
              setPlace('pages')
              void openPage(p)
            }}
            onPages={() => setPlace('pages')}
            /* A viewer reads the numbers and cannot change the promise made
               about them — the same three roles the server accepts, so the
               control is absent rather than present and refused. */
            mayConfigure={
              state.you?.role === 'owner' ||
              state.you?.role === 'admin' ||
              state.you?.role === 'editor'
            }
          />
        )}

        {place === 'library' && (
          <LibraryScreen
            you={state.you}
            branch={readOnly ? null : branch}
            onAddImage={() => setPlace('pages')}
          />
        )}

        {place === 'variables' && (
          <VariablesScreen
            branch={branch}
            /* Read-only only when it really is: somebody with view access, or
               a draft that has been finished. "There is no draft yet" stopped
               being one of those the moment the first save started making
               one, the way the editor has always done. */
            readOnly={readOnly}
            // The save staged variables.yaml; the review badge counts staged
            // changes, and it reads from the state this refreshes.
            onStaged={() => void refresh()}
            onOpenPage={(p) => {
              setPlace('pages')
              void openPage(p)
            }}
            {...(role === 'viewer' ? {} : { ensureDraft: () => startWriting({ silent: true }) })}
          />
        )}

        {place === 'words' && (
          <WordsScreen
            branch={branch}
            readOnly={readOnly}
            onStaged={() => void refresh()}
            onOpenPage={(p) => void openFile(p)}
            {...(role === 'viewer' ? {} : { ensureDraft: () => startWriting({ silent: true }) })}
          />
        )}

        {place === 'search' && (
          <SearchScreen
            state={state}
            branch={branch}
            readOnly={readOnly}
            onStaged={() => void refresh()}
            onOpenPage={(p) => void openFile(p)}
            {...(role === 'viewer' ? {} : { ensureDraft: () => startWriting({ silent: true }) })}
          />
        )}

        {place === 'redirects' && (
          <RedirectsScreen
            branch={branch}
            readOnly={readOnly}
            onStaged={() => void refresh()}
            {...(role === 'viewer' ? {} : { ensureDraft: () => startWriting({ silent: true }) })}
          />
        )}

        {place === 'settings' && (
          <SettingsScreen
            at={settingsAt}
            onAt={() => setSettingsAt(null)}
            onOpenPlace={setPlace}
            /**
             * A draft was started on a frozen version. Open it the way
             * picking a draft does — the writer asked to fix that version,
             * and leaving them on Settings would mean finding it themselves
             * in a picker full of drafts.
             */
            onEditVersion={(name) => {
              setBranch(name)
              setPlace('pages')
              void refresh(name)
            }}
            onChanged={() => {
              // Visibility or domain moved; the rail's lock marker and Home's
              // numbers read from this.
              void api
                .projects()
                .then((r) => {
                  setProjects(r.projects)
                  setMayAddSite(r.mayAddAnother ?? true)
                })
                .catch(() => {})
            }}
            onSignedOut={() => {
              // Revoking THIS browser's own session already told the server;
              // clearing local state is what makes the sign-in gate show up
              // instead of a shell whose every request now 401s.
              setSession(null)
            }}
            onDeleted={setDeleted}
          />
        )}

        {place === 'developers' && (
          <DevelopersScreen mayManage={workspaceRole === 'owner' || workspaceRole === 'admin'} />
        )}

        {place === 'help' && <HelpDocsScreen />}

        {place === 'import' && (
          <ImportScreen
            onOpenDraft={async (importBranch) => {
              /**
               * Load BEFORE switching screens. Reading a 26-page draft through a
               * real GitHub gateway takes seconds, and switching first showed the
               * old tree with the wrong branch for all of them — which reads as
               * "the import lied", measured at 11 silent seconds in a browser.
               * The report stays up with the button saying Opening… instead.
               */
              setBranch(importBranch)
              await refresh(importBranch)
              setPlace('pages')
            }}
          />
        )}

        {place === 'pages' && (
          <div className="body">
            {/* The tree is the SIDEBAR now — one column for the whole shell,
                not a second rail that only this place had. */}
            <main className="main">
              <div className="viewbar">
                {/*
                  Two ways to look at a page, and two more for the people who
                  need them.

                  Four tabs of equal weight said the four were equally likely,
                  and they are not: writing and checking how it reads are what
                  a day is made of, while the Markdown behind it and the
                  comparison against the live site are occasional. A row of
                  four also made the editor look like a tool with modes rather
                  than a document. `role="tablist"` stays on the pair, and the
                  other two keep their tab semantics inside the menu — the
                  keyboard reaches all four either way.
                */}
                <div className="segmented" role="tablist" aria-label="View">
                  {VIEWS.filter((v) => !v.fold).map((v) => (
                    <button
                      key={v.id}
                      role="tab"
                      aria-selected={view === v.id}
                      className={`segmented__btn${view === v.id ? ' is-active' : ''}`}
                      /* Same question as the surface chosen on open: these
                         files have no document form to switch to. */
                      disabled={v.id === 'blocks' && isSiteFile(path ?? '')}
                      onClick={() => setView(v.id)}
                    >
                      <Icon name={v.icon} size={13} />
                      {v.label}
                    </button>
                  ))}
                </div>
                <div className="viewmore">
                  <button
                    className={`btn btn--ghost btn--icon btn--sm${VIEWS.some((v) => v.fold && v.id === view) ? ' is-on' : ''}`}
                    aria-haspopup="menu"
                    aria-expanded={viewMenu}
                    data-view-more
                    data-tip="The source, and what has changed"
                    onClick={() => setViewMenu((open) => !open)}
                  >
                    <span className="nav-row__dots" aria-hidden>
                      ⋯
                    </span>
                    <span className="sr-only">Other views</span>
                  </button>
                  {viewMenu && (
                    <>
                      <div className="popover-scrim" onMouseDown={() => setViewMenu(false)} />
                      <div className="rowmenu" role="menu" aria-label="Other views">
                        {VIEWS.filter((v) => v.fold).map((v) => (
                          <button
                            key={v.id}
                            role="tab"
                            aria-selected={view === v.id}
                            className={`switcher__item${view === v.id ? ' is-on' : ''}`}
                            disabled={v.id === 'diff' && readOnly}
                            onClick={() => {
                              setViewMenu(false)
                              setView(v.id)
                            }}
                          >
                            <Icon name={v.icon} size={13} />
                            {v.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <span className="top__spacer" />

                {path && (
                  <nav className="crumbs" aria-label="Page path">
                    {path
                      .replace(/^pages\//, '')
                      .replace(/\.md$/, '')
                      .split('/')
                      .map((part, i, all) => (
                        <span key={i} className={i === all.length - 1 ? 'crumbs__leaf' : undefined}>
                          {i > 0 && <span className="crumbs__sep"> / </span>}
                          {part}
                        </span>
                      ))}
                  </nav>
                )}
                {/*
                  §22's marker, where the writer is rather than in a panel they
                  may not have open. It says UNREVIEWED, not "written by a
                  machine": accepting a suggestion is not reading it, and the
                  point of the marker is the gap between the two. The Inspector
                  is where it comes off.
                */}
                {frontmatter?.['ai_draft'] === true && (
                  <button
                    className="badge badge--warn badge--button"
                    data-ai-draft-badge
                    disabled={readOnly}
                    data-tip="A model wrote some of these words. Press this when you have read them."
                    onClick={() => void patchFrontmatter({ ai_draft: null } as FrontmatterPatch)}
                  >
                    <Icon name="alert" size={11} /> unreviewed suggestion
                  </button>
                )}
                {readOnly && (
                  <span className="badge badge--warn">
                    <Icon name="alert" size={11} /> read only
                  </span>
                )}
              </div>

              <div className="pane">
                {!path && (
                  <div className="empty">
                    <span className="empty__icon">
                      <Icon name="pages" size={20} />
                    </span>
                    <span className="empty__title">Pick a page</span>
                    <span className="empty__text">
                      Choose one on the left, or press <kbd>⌘</kbd> <kbd>K</kbd> to jump straight to
                      it.
                      {role === 'viewer'
                        ? ' You can read every page here; changing one is a writer’s job.'
                        : ' Your changes stay off the live site until they are merged and main is published.'}
                    </span>
                    {/* One action, and only where it can succeed.

                     It used to be "Start writing", whose whole job was to make
                     a draft — a step the product takes on the first keystroke
                     now, so the button had nothing left to do that typing does
                     not. What is left is the act somebody on an empty pane
                     actually wants, and it is not offered to a viewer, whom
                     the server would refuse. */}
                    {role !== 'viewer' && (
                      <button className="btn btn--tinted btn--sm" onClick={() => askForAPage()}>
                        <Icon name="plus" size={13} />
                        New page
                      </button>
                    )}
                  </div>
                )}

                {path && kept && saveState === 'clean' && !readOnly && (
                  <div className="keptbar">
                    <Icon name="alert" size={13} />
                    <span className="keptbar__what">
                      This browser kept an unsaved copy of this page from{' '}
                      {new Date(kept.at).toLocaleString()} — probably an offline edit or a closed
                      tab.
                    </span>
                    <button
                      className="btn btn--tinted btn--sm"
                      onClick={() => {
                        if (kept.doc) {
                          markDirtyDoc(kept.doc)
                        } else if (kept.text !== undefined) {
                          markDirtyText(kept.text)
                          setView('markdown')
                        }
                        setKept(null)
                      }}
                    >
                      Restore it
                    </button>
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        if (path) dropCopy(path)
                        setKept(null)
                      }}
                    >
                      Discard
                    </button>
                  </div>
                )}
                {path && view === 'blocks' && (
                  <>
                    {/*
                      The title and the description, where they will be read.

                      They were inspector fields, and the inspector is a panel
                      that leaves the screen below 1180px — so on a laptop in a
                      split window the two things a reader meets first could
                      not be edited at all. `nav.yaml` and the other whole-site
                      files have neither, and are not documents.
                    */}
                    {path.startsWith('pages/') && (
                      <DocHead
                        path={path}
                        frontmatter={frontmatter}
                        readOnly={readOnly}
                        onSave={patchFrontmatter}
                      />
                    )}
                    {/* The same help as the Markdown tab, on the surface that is
                        actually the default — see WritingHelp. */}
                    {state?.writingHelp && !readOnly && (
                      <WritingHelp
                        selection={blockSelection?.text ?? ''}
                        branch={branch}
                        markdownOf={() => contentToMarkdown(blockSelection?.content ?? [])}
                        onApply={async (next) => {
                          const where = blockSelection
                          if (!where) return
                          aiAccepted.current = true
                          blockHandle.current?.replaceRange(
                            where.from,
                            where.to,
                            await markdownToContent(next),
                          )
                        }}
                      />
                    )}
                    <BlockEditor
                      doc={doc}
                      readOnly={readOnly}
                      siteState={state}
                      branch={branch}
                      onChange={markDirtyDoc}
                      onSave={() => void save()}
                      {...(readOnly ? {} : { upload })}
                      onComment={(selection) => setPendingQuote(selection)}
                      onSelect={setBlockSelection}
                      onReady={(handle) => {
                        blockHandle.current = handle
                      }}
                    />
                  </>
                )}

                {path && view === 'markdown' && (
                  <Suspense fallback={<div className="empty">Loading the text editor…</div>}>
                    {/*
                      Help with the words, above the text rather than floating
                      over it: a bar that appears where the selection is covers
                      the sentence being worked on, and the suggestion needs
                      room to be read beside what it replaces.

                      Offered only where it is allowed — the project's switch,
                      a deployment with a provider, and somebody who may write
                      here. `state.writingHelp` is the first two; `readOnly` is
                      the third.
                    */}
                    {state?.writingHelp && !readOnly && (
                      <WritingHelp
                        selection={selection}
                        branch={branch}
                        markdownOf={async () => selection}
                        onApply={(next) => {
                          aiAccepted.current = true
                          editorHandle.current?.replaceSelection(next)
                        }}
                      />
                    )}
                    <Editor
                      value={content}
                      onChange={markDirtyText}
                      onSave={() => void save()}
                      readOnly={readOnly}
                      onSelect={setSelection}
                      onReady={(handle) => {
                        editorHandle.current = handle
                      }}
                    />
                  </Suspense>
                )}

                {path && view === 'preview' && (
                  <div
                    className="preview"
                    ref={previewRef}
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                )}

                {view === 'diff' && <DiffView diff={diff} />}
              </div>
            </main>

            <Inspector
              path={path ?? ''}
              branch={branch}
              frontmatter={frontmatter}
              problems={pageProblems}
              people={people}
              disabled={readOnly || !path}
              onSave={patchFrontmatter}
              onRename={rename}
              onDelete={remove}
            />
            {path && (
              <CommentsPanel
                path={path}
                branch={branch}
                readOnly={readOnly}
                /*
                 * Saying something is not editing. Spec 7 gives a viewer
                 * "read accessible private content and optionally comment",
                 * and this panel took the editor's own flag for both — so the
                 * person invited precisely to give an opinion was shown a
                 * conversation with no way into it. A finished draft is still
                 * closed to everybody: there is nothing left to say about a
                 * branch that has been published.
                 */
                mayComment={closed !== 'merged' && closed !== 'closed'}
                pending={pendingQuote}
                onPendingUsed={() => setPendingQuote(null)}
                onChanged={() => {
                  void api
                    .commentCounts(branch)
                    .then((r) => setCommentCounts(r.counts))
                    .catch(() => undefined)
                }}
              />
            )}
          </div>
        )}
      </div>

      <CommandPalette
        open={paletteOpen}
        commands={commands}
        onClose={() => setPaletteOpen(false)}
        branch={branch}
        onOpenHit={(hit) => {
          setPlace('pages')
          void openPage(hit.path)
        }}
        /* A search that finds nothing is not an error, it is a page that does
           not exist yet — so the one thing offered is making it. A viewer is
           offered nothing, because the server would refuse both of these. */
        {...(role === 'viewer'
          ? {}
          : {
              emptyAction: { label: 'New page', icon: 'plus' as const, run: () => askForAPage() },
            })}
      />
      {/* One tooltip for every `data-tip` in the product — see TooltipLayer. */}
      <TooltipLayer />
      {tourRunning && TOURS[tourRunning] && (
        <Tour
          surface={tourRunning}
          stops={TOURS[tourRunning].stops}
          onDone={(finished) => endTour(tourRunning, finished)}
        />
      )}
      <Shortcuts open={helpOpen} onClose={() => setHelpOpen(false)} />
      <HelpPanel
        open={helpPanelOpen}
        place={place}
        onClose={() => setHelpPanelOpen(false)}
        onShortcuts={() => {
          setHelpPanelOpen(false)
          setHelpOpen(true)
        }}
        onDocs={() => {
          setHelpPanelOpen(false)
          setPlace('help')
        }}
        onTour={(surface) => {
          setHelpPanelOpen(false)
          setTourRunning(surface)
        }}
      />
      {/*
        The three questions this shell used to ask through the operating
        system. One dialog, because they are one shape: a name, the rule the
        name obeys, and a sentence saying what pressing the button makes.
      */}
      {making === 'site' && (
        <CreateDialog
          title="New docs site"
          icon="pages"
          what="A second documentation site in this workspace: its own repository, its own drafts and its own published address. Nothing here moves into it."
          submitLabel="Create the site"
          fields={[
            {
              key: 'name',
              label: 'Name',
              placeholder: 'The API reference',
              note: 'What this documentation is called. Readers see it, and its address is made from it.',
            },
          ]}
          initial={{ name: '' }}
          onClose={() => setMaking(null)}
          onSubmit={async (values) => {
            await createProject(values['name'] ?? '')
            setMaking(null)
          }}
        />
      )}
      {making === 'draft' && (
        <CreateDialog
          title="New draft"
          icon="branch"
          what="Writing happens on a draft. Edits stage there until you commit them, and the live site stays read only until you publish."
          submitLabel="Start the draft"
          fields={[
            {
              key: 'name',
              label: 'Name',
              mono: true,
              note: 'Suggested from your name and today’s date. Change it when the draft is about one thing worth naming.',
            },
          ]}
          initial={{ name: suggestedBranch() }}
          onClose={() => setMaking(null)}
          onSubmit={async (values) => {
            await createBranch((values['name'] ?? '').trim())
            setMaking(null)
          }}
        />
      )}
      {making === 'page' && (
        <CreateDialog
          title="New page"
          icon="page"
          what="Staged on this draft, ready to write in, and listed where you put it. Publishing it is a separate step."
          submitLabel="Create the page"
          fields={[
            {
              key: 'title',
              label: 'Title',
              placeholder: 'Getting started',
              note: 'The heading on the page, the words a reader sees in search results, and what the navigation lists it as.',
              /* The address, while it is being typed. It comes from the title
                 and from where the page is placed, so it cannot be shown as a
                 field somebody edits — but it can be shown, and a person about
                 to make a page has every right to know the link they are
                 making. `slugFor` is the same function that makes it. */
              derived: (values) => {
                const said = (values['title'] ?? '').trim()
                if (said === '') return null
                const chosen = places[Number(values['place'] ?? '0')]
                const parent = chosen
                  ? itemAt(state.nav, chosen.target.containerPath)?.page
                  : undefined
                const id = parent ? `${parent}/${slugFor(said)}` : slugFor(said)
                return `Readers will find it at /${id}/`
              },
            },
            {
              key: 'place',
              label: 'Place under',
              choices:
                places.length > 0
                  ? places.map((place, index) => ({
                      value: String(index),
                      label: place.label,
                      indent: place.depth,
                    }))
                  : /* A workspace is seeded with no sections at all, so the
                       first page anybody adds has nowhere to go. Rather than
                       an empty picker, the sidebar's first section is offered
                       — named after the site, which is how most documentation
                       reads anyway. */
                    [{ value: '-1', label: `A new section: ${siteName ?? 'Documentation'}` }],
              note: 'Where it sits in the navigation. A page the navigation does not list cannot be published, so this is asked now rather than reported later.',
            },
          ]}
          /* `-1` when there are no sections yet, because that is the value of
             the only choice offered then — a default that names no option
             leaves the select showing one thing and holding another, and the
             page lands unlisted. */
          initial={{
            title: '',
            place: places.length > 0 ? String(Math.max(0, placeFromRow)) : '-1',
          }}
          onClose={() => {
            setMaking(null)
            setPlaceUnder(null)
          }}
          onSubmit={async (values) => {
            await createPage(values['title'] ?? '', Number(values['place'] ?? '0'))
            setMaking(null)
            setPlaceUnder(null)
          }}
        />
      )}
      {making === 'section' && (
        <CreateDialog
          title="New section"
          icon="nav"
          what="A heading in the sidebar. Pages go inside it, and readers walk the sections in the order they appear here."
          submitLabel="Add the section"
          fields={[
            {
              key: 'label',
              label: 'Name',
              placeholder: 'Guides',
              note: 'What readers see above the pages it holds.',
            },
          ]}
          initial={{ label: '' }}
          onClose={() => setMaking(null)}
          onSubmit={async (values) => {
            await createSection(values['label'] ?? '')
            setMaking(null)
          }}
        />
      )}
      <History
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        mayPublish={session?.workspaces?.find((w) => w.id === workspace?.id)?.canPublish ?? false}
        {...(role === 'viewer' ? {} : { onWrite: () => setPlace('pages') })}
        onRolledBack={() => {
          // The published branch moved, so what the editor is showing may be
          // stale and the inbox certainly is.
          void refresh(branch)
          void api
            .scan()
            .then(() => api.inbox(['open']))
            .then((body) => setInboxCount(body.counts['open'] ?? 0))
            .catch(() => undefined)
        }}
      />

      <TrashDrawer
        open={trashOpen}
        onClose={() => setTrashOpen(false)}
        branch={branch}
        mayWrite={!readOnly}
        mayPurge={role === 'owner' || role === 'admin'}
        onRestored={(restoredPath, placed) => {
          void refresh().then(() => openPage(restoredPath))
          /**
           * The delete wrote down where the page sat, and a restore puts it
           * back there when its neighbours are still in place. When they are
           * not, it is the same state a new page is in — staged, and the
           * navigation does not list it — and placing it is the person's
           * call; saying so beats letting the commit refuse them later.
           */
          if (placed) {
            toasts.success('Restored where it was', 'It is back in the navigation on this draft.')
          } else {
            toasts.warn(
              'Restored, but not in the navigation',
              'The place it used to sit is gone. Drag it into the tree on the left, or publishing will be refused.',
              /*
               * Sticky, and no button: the drawer this fires from already
               * closes onto the tree, and the tree is where the page is
               * dragged. A button that opened `nav.yaml` as a page was a door
               * into the raw file — and, on the shell this product has now,
               * into a wall.
               */
              { sticky: true },
            )
          }
          setTrashOpen(false)
          setPlace('pages')
        }}
      />

      <Inbox
        open={inboxOpen}
        onClose={() => setInboxOpen(false)}
        onOpenPage={(p) => void openPage(p)}
        onCounts={(counts) => setInboxCount(counts['open'] ?? 0)}
        onPulse={() => setPlace('pulse')}
        branch={branch}
        canDraft={Boolean(state?.writingHelp) && !readOnly}
        /**
         * The one way a drafted change reaches a page: an ordinary write, on the
         * draft the writer is on, marked as carrying a suggestion nobody has
         * reviewed — §22's marker, because that is exactly what it is.
         */
        onSaveDraft={async (drafted, content) => {
          if (!branch) throw new Error('Choose a draft first.')
          await api.write(branch, drafted, content, undefined, true)
          await refresh()
          // Reload it if it is the page on screen, so the editor shows what was
          // written rather than what was there a moment ago.
          if (drafted === path) await openPage(drafted)
          toasts.success('Saved to this draft', 'Read it in Diff before you commit.')
        }}
      />

      {conflict && path && (
        <ConflictView
          path={path}
          mine={conflict.mine}
          theirs={conflict.theirs}
          onTakeTheirs={() => {
            // A decision, taken on a button that says what it costs.
            pendingDoc.current = null
            pendingText.current = null
            dropCopy(path)
            setKept(null)
            setConflict(null)
            setSaveState('clean')
            void openPage(path)
          }}
          onKeepMine={() => {
            // Adopt their revision so the save is no longer stale, then send
            // mine — an overwrite chosen with both versions on screen.
            revision.current = conflict.revision
            /**
             * And say so, so the person whose version this replaces is told.
             *
             * Nothing else sends this: from the server's side the request that
             * follows is an ordinary save whose base revision is current, which
             * is exactly why the discarded author was never told anything.
             */
            replacing.current = conflict.revision
            if (pendingText.current === null && pendingDoc.current === null) {
              pendingText.current = content
            }
            setConflict(null)
            setSaveState('dirty')
            void save()
          }}
          onClose={() => setConflict(null)}
        />
      )}
      <PublishSheet
        open={publishOpen}
        branch={branch}
        staged={state.staged}
        problems={state.lint.problems}
        titles={new Map(state.pages.map((p) => [p.id, p.title ?? p.id]))}
        nav={state.nav}
        people={people}
        youId={session?.user?.id ?? null}
        busy={busy}
        mayCommit={role !== 'viewer'}
        aimedAt={aimedAt}
        onClose={() => setPublishOpen(false)}
        onCommit={(note) => void commitNow(note)}
        onSendForReview={(note, reviewerId) => void sendForReview(note, reviewerId)}
        onOpenPage={(p) => {
          setPlace('pages')
          void openPage(p)
        }}
      />
      <ReviewDrawer
        open={reviewOpen}
        branch={branch}
        branches={state.branches}
        onPickDraft={(next) => {
          setBranch(next)
          void refresh(next).then(() => {
            if (path) void openPage(path)
          })
        }}
        onNewDraft={() => {
          // One question at a time: the drawer closes, the dialog asks.
          setReviewOpen(false)
          setMaking('draft')
        }}
        staged={state.staged}
        problems={state.lint.problems}
        diff={diff}
        busy={busy}
        pr={
          branchInfo?.prNumber
            ? { number: branchInfo.prNumber, url: branchInfo.prUrl ?? '#' }
            : null
        }
        siteUrl={siteAddress}
        youId={session?.user?.id ?? null}
        people={people}
        mayVerdict={
          role === 'owner' ||
          role === 'admin' ||
          (state.you?.canReview ?? false) ||
          (session?.workspaces?.find((w) => w.id === workspace?.id)?.canPublish ?? false)
        }
        committed={Boolean(branchInfo?.headSha)}
        aimedAt={aimedAt}
        onClose={() => setReviewOpen(false)}
        onCommit={(message) => void commit(message)}
        onPush={() => void push()}
        {...(workspace && session?.workspaces?.find((w) => w.id === workspace.id)?.canPublish
          ? { onMerge: () => void mergeDraft() }
          : {})}
        onUnstage={(p) => void unstage(p)}
        {...(role === 'viewer' ? {} : { onWrite: () => setPlace('pages') })}
        {...(role === 'viewer' ? {} : { onTidyRedirects: () => void tidyRedirects() })}
      />
      {/* After the review drawer, for the same reason ConfirmIdentity is last:
          the commit was pressed THERE, so this scrim has to stack above it —
          and closing this one has to leave the writer back where they were. */}
      {moved && (
        <ConflictView
          /* Keyed on the head it describes, so a second refusal arrives as a
             fresh screen rather than as new files under old choices. */
          key={moved.report.actual}
          kind="commit"
          report={moved.report}
          busy={busy}
          onResolve={(choices) => void resolveMoved(choices)}
          onClose={() => setMoved(null)}
        />
      )}
      {mergeConflict && (
        <MergeConflictView
          paths={mergeConflict}
          busy={busy}
          onResolve={(choices) => void resolveMerge(choices)}
          onClose={() => setMergeConflict(null)}
        />
      )}
      <Toasts toasts={toasts.toasts} onDismiss={toasts.dismiss} />
      {/* Last, so its scrim stacks above any drawer that raised the question. */}
      <ConfirmIdentity />
    </div>
  )

  // ---------------------------------------------------------------- actions

  /**
   * Get somebody writing, without asking them to name anything.
   *
   * The same thing `newBranch` does, minus the prompt. A prompt is right when a
   * person has a reason to choose — several branches in flight — and wrong as the
   * first thing anybody meets, when they have no basis for the choice and every
   * name is as good as any other. They can still branch explicitly whenever they
   * want a name that means something.
   */
  /**
   * Start from a template: the pages this kind of documentation needs.
   *
   * Offered in two places on purpose — the first run, and the Home checklist
   * for somebody who skipped it. A capability with one door on one screen that
   * only appears once is a capability most people never meet.
   *
   * Write first, land second, the same lesson as the import report: switching
   * screens before the draft exists shows an empty tree for the seconds the
   * write takes, which reads as "the template did nothing".
   */
  async function startFromTemplate(
    useCase: 'product' | 'api' | 'help' | 'handbook' | null,
    before?: () => void,
  ): Promise<void> {
    try {
      const out = await api.startFromTemplate(useCase ?? undefined)
      setBranch(out.branch)
      await refresh(out.branch)
      before?.()
      setPlace('pages')
      if (out.written[0]) await openPage(out.written[0], out.branch).catch(() => undefined)
      toasts.success(
        `${out.written.length} pages on ${out.branch}`,
        'Prompts, not filler — rewrite them and publish when they are true.',
      )
    } catch (e) {
      // A failed template is not a failed anything else: land in the editor,
      // which is where the person was going.
      before?.()
      setPlace('pages')
      toasts.error(
        'Could not start from the template',
        because(
          e,
          `Nothing was written. Try again, or start with a blank page and build the sections yourself.`,
        ),
      )
    }
  }

  /**
   * The switcher menu's Sign out. The server revokes this browser's session;
   * clearing the local one is what brings the gate up instead of a shell
   * whose every request now answers 401.
   */
  async function signOutEverywhere(): Promise<void> {
    await api.signOut().catch(() => {})
    setState(null)
    setBootError(null)
    setSession(null)
  }

  /**
   * Start somewhere to write, and return it.
   *
   * `silent` is the first-keystroke path: the writer did not ask for a draft
   * and does not need to be told they got one — being congratulated on a
   * branch you did not create is exactly the machinery this hides. It also
   * skips re-opening the page, because the save that called it is mid-flight
   * and re-reading underneath it would fight its own write.
   */
  async function startWriting(opts: { silent?: boolean } = {}): Promise<string | null> {
    if (!state) return null
    const stamp = new Date().toISOString().slice(0, 10)
    const who = (session?.user.displayName ?? 'draft')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    const base = `draft/${who || 'writer'}/${stamp}`
    // A suffix only if that name is taken, so the common case reads cleanly.
    const taken = new Set(state.branches.map((b) => b.name))
    let name = base
    for (let n = 2; taken.has(name); n++) name = `${base}-${n}`

    try {
      await api.createBranch(name)
      setBranch(name)
      if (opts.silent) {
        // The state is refreshed by the save that follows; asking here would
        // read the branch before the write has landed on it.
        return name
      }
      await refresh(name)
      if (path) await openPage(path, name)
      toasts.success(`Working on ${name}`, 'Edits stage here until you commit them.')
      return name
    } catch (e) {
      toasts.error(
        'Could not start writing',
        because(
          e,
          `Nothing was saved. Try again — if it keeps refusing, you may have view access only.`,
        ),
      )
      return null
    }
  }

  /**
   * A draft with a name somebody chose.
   *
   * `startWriting` above makes one without asking; this is the other half of
   * that comment — the writer who has a reason to name it. The refusal is
   * thrown rather than toasted, so the dialog keeps it and keeps the name.
   */
  async function createBranch(name: string) {
    await api.createBranch(name)
    setBranch(name)
    await refresh(name)
    if (path) await openPage(path, name)
    toasts.success(`Working on ${name}`, 'Edits stage here until you commit them.')
  }

  /**
   * A draft name nothing is using yet.
   *
   * The prompt this replaces suggested `draft/writer/<today>` — not the
   * writer's name, and not checked against the drafts that exist, so pressing
   * New draft twice in one day and accepting the offer was refused by the
   * server for a name the product had just proposed. This is the name
   * `startWriting` would have made.
   */
  function suggestedBranch(): string {
    const stamp = new Date().toISOString().slice(0, 10)
    const who = (session?.user.displayName ?? 'draft')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    const base = `draft/${who || 'writer'}/${stamp}`
    const taken = new Set((state?.branches ?? []).map((b) => b.name))
    let name = base
    for (let n = 2; taken.has(name); n++) name = `${base}-${n}`
    return name
  }

  /**
   * Write a page, and LIST it.
   *
   * The old version asked for an address, wrote the file, and then warned the
   * writer that what they had just made could not be published until they
   * edited a YAML file — which is the product creating a problem and handing
   * it back. The dialog asks where it goes instead, and this puts it there:
   * one write for the page, one for the navigation, in that order, so a
   * failure to place it still leaves the words safe.
   *
   * The address comes from the title. It is the reader's address, so it is
   * worth being deliberate about — but "worth being deliberate about" is not
   * the same as "worth asking a writer to invent before they have written a
   * word", and a page can be moved later with its links repointed.
   */
  async function createPage(title: string, placeIndex: number) {
    // A draft if there is not one yet, the way the first keystroke makes one.
    const on = branch ?? (await startWriting({ silent: true }))
    if (!on) throw new Error('Could not start somewhere to write.')
    const named = title.trim() === '' ? 'New page' : title.trim()
    /**
     * `-1` is the answer the picker offers when the navigation has no
     * sections: make one, then place the page in it. Two writes to nav.yaml
     * would race each other, so the section is made in the same pass below.
     */
    const startSection = placeIndex < 0 ? (siteName ?? 'Documentation') : null
    const place = startSection ? null : (places[placeIndex] ?? places[0])

    /** The parent page's id, when the chosen place is a page rather than a section. */
    const under = place ? itemAt(state!.nav, place.target.containerPath)?.page : undefined
    const slug = slugFor(named)
    const base = under ? `${under}/${slug}` : slug
    // A name nothing is using yet, so the write cannot fail on a collision the
    // product could have avoided.
    const taken = new Set(state!.pages.map((p) => p.id))
    let id = base
    for (let n = 2; taken.has(id); n++) id = `${base}-${n}`
    const target = `pages/${id}.md`

    await api.write(on, target, NEW_PAGE(named))

    /**
     * And into the navigation, read fresh for the reason every other write to
     * it is: this file is the one thing everybody's edits share.
     *
     * Unconditional now. This used to be skipped whenever the picker had
     * nothing to offer, and the read used to be abandoned whenever the file
     * could not be read — which on a repository with no `nav.yaml` in it
     * meant every page ever made there was born an orphan, and told so by a
     * warning it could do nothing about. A missing navigation is an EMPTY
     * one: writing it is the placement.
     */
    /**
     * Place it, and say where it landed.
     *
     * A function rather than a block, because it is also what the warning's
     * button does. A toast that says "put it in the navigation" and offers a
     * door somewhere else is a toast handing back the job it just failed at;
     * the button here finishes it.
     */
    const placeIt = async (): Promise<string | null> => {
      const current = await api
        .page(NAV_PATH, on)
        .catch(() => ({ content: '', revision: null as string | null }))
      const before = parseNav(current.content)
      /*
       * A section to land in, whichever way we got here: the one the writer
       * picked, or a first one named after the site when there are none. The
       * picker offers "-1" for that case, and a picker with no options at all
       * leaves `place` undefined — both mean the same thing to the file.
       */
      const naming = startSection ?? (siteName || 'Documentation')
      const needSection = !place && before.sections.length === 0
      const started =
        startSection || needSection ? addNavSection(before, naming) : { ok: false as const }
      const base = started.ok ? (started.nav as Nav) : before
      const to = place?.target ?? {
        containerPath: [Math.max(0, base.sections.length - 1)],
        beforeIndex: 0,
      }
      const outcome = insertNavItem(base, { page: id }, to)
      if (!outcome.ok) return null
      await api.write(
        on,
        NAV_PATH,
        serializeNav(outcome.nav as Nav, current.content),
        current.revision ?? undefined,
      )
      return place?.label ?? naming
    }

    /*
     * Unconditional now. This used to be skipped whenever the picker had
     * nothing to offer, and abandoned whenever `nav.yaml` could not be read —
     * which on a repository without one meant every page made there was born
     * an orphan and told so by a warning it could do nothing about. A missing
     * navigation is an EMPTY one: writing it is the placement.
     */
    let landedIn: string | null = null
    try {
      landedIn = await placeIt()
    } catch {
      // Reported below rather than thrown: the page itself is written, and
      // losing the placement is a smaller thing than losing the words.
    }

    await refresh(on)
    await openPage(target, on)
    if (landedIn) {
      toasts.success(`${named} is in ${landedIn}`, 'Write it, then publish when it is true.')
    } else {
      toasts.warn(
        'Page saved, but not listed',
        'A page nothing lists cannot be published. This puts it at the end of the navigation.',
        // Sticky, and the button finishes the job rather than pointing at it:
        // the page cannot be published until this is done, so it must not
        // vanish while somebody is still writing.
        {
          sticky: true,
          action: {
            label: 'Put it in the navigation',
            onClick: () => {
              void placeIt()
                .then(async (where) => {
                  await refresh(on)
                  if (where) toasts.success(`${named} is in ${where}`, 'It can be published now.')
                  else
                    toasts.error(
                      'Still not listed',
                      'The navigation would not take it. Open the tree and drag it where it belongs.',
                    )
                })
                .catch((e) =>
                  toasts.error(
                    'Still not listed',
                    because(e, `Open the tree and drag it where it belongs.`),
                  ),
                )
            },
          },
        },
      )
    }
  }

  /**
   * Rename a row: what the NAVIGATION lists it as, not the page's own title.
   *
   * Two different names, and telling them apart is the point. A group has
   * only this one; a page has a title of its own and may be listed under
   * something shorter, which is what an empty answer here goes back to.
   */
  async function renameInNav(at: NavPath, label: string) {
    const on = await somewhereToWrite()
    if (!on) return
    try {
      const current = await api.page(NAV_PATH, on)
      const outcome = renameNavItem(parseNav(current.content), at, label)
      if (!outcome.ok) {
        if (outcome.reason === 'not-found') {
          toasts.warn('The navigation changed underneath that', 'Reloading — try again.')
          await refresh(on)
        }
        return
      }
      await api.write(
        on,
        NAV_PATH,
        serializeNav(outcome.nav as Nav, current.content),
        current.revision,
      )
      await refresh(on)
    } catch (e) {
      toasts.error(
        'Could not rename that',
        because(e, `The old name is still in place. Try again.`),
      )
    }
  }

  /**
   * Somewhere to write, whether or not there is one yet.
   *
   * Every edit in this product goes onto a draft, and since the first
   * keystroke makes one, the rest of the writing surfaces must not sit there
   * refusing quietly because they were written when a draft was something a
   * person chose. Each of the callers below used to begin `if (!branch)
   * return` — a silent no-op, which is the worst answer available: the drag
   * happened, the row moved back, and nothing said why.
   */
  async function somewhereToWrite(): Promise<string | null> {
    return branch ?? (await startWriting({ silent: true }))
  }

  /** Change a section's heading, which is the one name it has. */
  async function renameSectionInNav(index: number, label: string) {
    const on = await somewhereToWrite()
    if (!on) return
    try {
      const current = await api.page(NAV_PATH, on)
      const outcome = renameNavSection(parseNav(current.content), index, label)
      if (!outcome.ok) return
      await api.write(
        on,
        NAV_PATH,
        serializeNav(outcome.nav as Nav, current.content),
        current.revision,
      )
      await refresh(on)
    } catch (e) {
      toasts.error(
        'Could not rename that section',
        because(e, `The old name is still in place. Try again.`),
      )
    }
  }

  /**
   * Remove a section, and say why when it cannot go.
   *
   * The engine refuses one that still holds pages — they would become orphans
   * and the commit would be refused for a reason nothing on screen explains.
   * That refusal is a sentence here rather than a silent no-op.
   */
  async function removeSectionFromNav(index: number) {
    const on = await somewhereToWrite()
    if (!on) return
    try {
      const current = await api.page(NAV_PATH, on)
      const outcome = removeNavSection(parseNav(current.content), index)
      if (!outcome.ok) {
        if (outcome.reason === 'not-empty') {
          toasts.warn(
            'That section still holds pages',
            'Move them somewhere else first — a page nothing lists cannot be published.',
          )
        }
        return
      }
      await api.write(
        on,
        NAV_PATH,
        serializeNav(outcome.nav as Nav, current.content),
        current.revision,
      )
      await refresh(on)
      toasts.success('Section removed', 'The sidebar has one fewer heading.')
    } catch (e) {
      toasts.error(
        'Could not remove that section',
        because(e, `The section is still there. Try again, or empty it first.`),
      )
    }
  }

  /** Add a heading to the sidebar, from the section menu. */
  async function createSection(label: string) {
    const on = branch ?? (await startWriting({ silent: true }))
    if (!on) throw new Error('Could not start somewhere to write.')
    const current = await api.page(NAV_PATH, on)
    const outcome = addNavSection(parseNav(current.content), label)
    if (!outcome.ok) throw new Error('Give the section a name.')
    await api.write(
      on,
      NAV_PATH,
      serializeNav(outcome.nav as Nav, current.content),
      current.revision,
    )
    await refresh(on)
    toasts.success(`${label.trim()} is in the sidebar`, 'Put pages in it from any row’s ＋.')
  }

  /**
   * A copy of a page, beside the original and listed after it.
   *
   * The everyday way a second one of something gets written — a new endpoint
   * page that is the last endpoint page with the words changed — and until
   * now it meant opening the page, selecting everything, making a page, and
   * pasting.
   */
  async function duplicatePage(path: string) {
    const on = await somewhereToWrite()
    if (!on) return
    try {
      const source = await api.page(path, on)
      const id = path.replace(/^pages\//, '').replace(/\.md$/, '')
      const taken = new Set(state?.pages.map((p) => p.id) ?? [])
      let copy = `${id}-copy`
      for (let n = 2; taken.has(copy); n++) copy = `${id}-copy-${n}`
      const target = `pages/${copy}.md`
      // The title carries "(copy)" too, or the tree shows two identical rows
      // and the writer has to open both to find out which is which.
      const content = source.content.replace(
        /^title:\s*(.*)$/m,
        (_line, was: string) => `title: ${was.trim()} (copy)`,
      )
      await api.write(on, target, content)

      // Listed immediately after the page it was copied from, when that page
      // is in the navigation at all.
      const current = await api.page(NAV_PATH, on)
      const nav = parseNav(current.content)
      const at = findNavPath(nav, id)
      if (at) {
        const outcome = insertNavItem(
          nav,
          { page: copy },
          {
            containerPath: at.slice(0, -1),
            beforeIndex: at[at.length - 1]! + 1,
          },
        )
        if (outcome.ok) {
          await api.write(
            on,
            NAV_PATH,
            serializeNav(outcome.nav as Nav, current.content),
            current.revision,
          )
        }
      }
      await refresh(on)
      await openPage(target, on)
      toasts.success('Copied', 'The copy is open — rename it and change what differs.')
    } catch (e) {
      toasts.error(
        'Could not copy that page',
        because(e, `Nothing was copied and the original is untouched. Try again.`),
      )
    }
  }

  /**
   * Move a page to the trash from the tree, taking its navigation row with it.
   *
   * The inspector's own delete asks about the links that would break and is
   * the right surface for a page somebody is reading. This is the one for a
   * page somebody is looking AT, in the list, and it goes through the same
   * server call — including the refusal, which is reported rather than forced.
   */
  async function trashPage(target: string) {
    const on = await somewhereToWrite()
    if (!on) return
    try {
      const result = await api.remove(on, target)
      // The editor cannot go on showing a page that is in the trash.
      if (target === path) {
        setPath(null)
        setDoc(null)
        setContent('')
        setSavedContent('')
      }
      await refresh(on)
      const broken = result.brokeLinksOn.length
      if (broken > 0) {
        toasts.warn(
          'Moved to trash, and something is broken',
          `${broken} page${broken === 1 ? '' : 's'} still link${broken === 1 ? 's' : ''} to it. Fix those before you publish.`,
        )
      } else {
        toasts.success(
          'Moved to trash',
          result.nav
            ? 'Taken out of the navigation too. It can be put back from Trash.'
            : 'It can be put back from Trash.',
        )
      }
    } catch (e) {
      if (e instanceof RequestFailed && e.code === 'page_in_use') {
        const pages = Array.isArray(e.body['pages']) ? (e.body['pages'] as string[]) : []
        toasts.warn(
          'Other pages link to that one',
          `${pages.slice(0, 3).join(', ')}${pages.length > 3 ? '…' : ''} — open it and delete it from the panel, where the whole list is.`,
        )
        return
      }
      toasts.error(
        'Could not move that page',
        because(
          e,
          `The page is untouched. Try again, or open it and delete it from the panel, where the links it would break are listed.`,
        ),
      )
    }
  }

  /**
   * Ask for a page.
   *
   * There is no draft to check for any more: `createPage` makes one the way
   * every other write does. The only refusal left belongs to the server, and
   * it is about who may write rather than about where.
   */
  function askForAPage() {
    if (role === 'viewer') {
      return toasts.warn('You have view access', 'Ask an admin for editor access to make changes.')
    }
    setMaking('page')
  }

  /**
   * Move the page. The panel that asked keeps the refusal.
   *
   * Thrown rather than toasted: the question is asked inside the inspector
   * now, so the answer belongs there too — beside the address that was typed,
   * which somebody is usually one character away from fixing.
   */
  async function rename(to: string) {
    if (!branch || !path || to === path) return
    {
      const result = await api.move(branch, path, to)
      await refresh()
      await openPage(to)
      /**
       * Say what it did. This used to warn "update nav.yaml as well", which was
       * true then and is a lie now — and a move touches other people's pages, so
       * the writer needs to know which before they review the diff.
       */
      const done = [
        result.links > 0
          ? `${result.links} link${result.links === 1 ? '' : 's'} repointed on ` +
            `${result.relinked.length} page${result.relinked.length === 1 ? '' : 's'}`
          : 'nothing linked to it',
        result.nav ? 'navigation updated' : 'not in the navigation',
        'the old address will redirect',
      ]
      /**
       * Only when there was something to say. A rename carries the review
       * conversation and the search tuning as well, and both are invisible —
       * a thread nobody can see move, a pin nobody can see repoint — so the
       * product says it did them rather than letting somebody wonder.
       */
      if (result.comments > 0) {
        done.push(
          `${result.comments} conversation${result.comments === 1 ? '' : 's'} moved with it`,
        )
      }
      if (result.search > 0) {
        done.push(`${result.search} search ${result.search === 1 ? 'entry' : 'entries'} repointed`)
      }
      /**
       * And a site-level redirect, which is the most invisible of the three: a
       * migration redirect that quietly became a chain would be found by a
       * reader taking two hops, not by anybody in this product.
       */
      if (result.redirectsUpdated) done.push('a redirect now aims at the new address')
      toasts.success('Renamed', `${done.join(', ')}.`)
    }
  }

  /**
   * Move the page to the trash.
   *
   * Returns the pages that link to it when the server refuses, and null when
   * it is gone. Two `window.confirm`s used to stand here: the question, and
   * the list. Both are asked in the panel now — see `Inspector`.
   */
  async function remove(force: boolean): Promise<string[] | null> {
    if (!branch || !path) return null

    /**
     * Two steps, because the interesting answer is the second one.
     *
     * The server refuses while other pages link to it and says which. That list
     * is the whole point: unlike a rename there is nowhere to repoint those
     * links, so this is a decision somebody makes — and one they should not make
     * without knowing what breaks. The old version staged the delete regardless
     * and told the writer to "remove it from nav.yaml too", which is now done
     * for them.
     */
    const done = (result: { brokeLinksOn: string[]; nav: boolean }) => {
      setPath(null)
      setDoc(null)
      setContent('')
      setSavedContent('')
      void refresh()
      const broken = result.brokeLinksOn.length
      if (broken > 0) {
        toasts.warn(
          'Moved to trash, and something is broken',
          `${broken} page${broken === 1 ? '' : 's'} still link${broken === 1 ? 's' : ''} to it. ` +
            'Fix those before you publish.',
        )
      } else {
        toasts.success(
          'Moved to trash',
          result.nav
            ? 'Removed from the navigation too. Restore it from Trash, under Pages.'
            : 'Nothing linked to it. Restore it from Trash, under Pages.',
        )
      }
    }

    try {
      done(await api.remove(branch, path, force || undefined))
      return null
    } catch (e) {
      /*
       * Not a failure — a question. The panel turns this list into the second
       * step; an alert used to print it, unscrollable and gone the moment it
       * was answered, over the panel that could have shown it.
       */
      if (!force && e instanceof RequestFailed && e.code === 'page_in_use') {
        return Array.isArray(e.body['pages']) ? (e.body['pages'] as string[]) : []
      }
      throw e
    }
  }

  async function unstage(target: string) {
    if (!branch) return
    try {
      await api.unstage(branch, target)
      await refresh()
      await loadDiff()
      if (target === path) await openPage(target)
      toasts.info('Discarded', target)
    } catch (e) {
      toasts.error(
        'Could not discard that change',
        because(e, `The change is still here, so nothing is lost. Try again.`),
      )
    }
  }

  /**
   * Remove the `redirect_from` entries that cannot work.
   *
   * The problems it clears BLOCK a publish — a page claiming an address another
   * page now answers, or its own — and an import can arrive with either
   * already in it. The repair existed in the core from the start with no door,
   * so the way past was to open every page and edit YAML by hand.
   *
   * Staged like any other edit, then the diff is reloaded: the writer reads
   * what it did before committing it, which is the difference between a repair
   * and a program changing pages behind somebody's back.
   */
  async function tidyRedirects() {
    if (!branch) return
    setBusy(true)
    try {
      const out = await api.tidyRedirects(branch)
      await refresh()
      await loadDiff()
      if (out.collapsed.length === 0) {
        toasts.info('Nothing to tidy', 'No redirect entry here points at a page that exists.')
      } else {
        toasts.success(
          `Removed ${out.collapsed.length} redirect${out.collapsed.length === 1 ? '' : 's'}`,
          `On ${out.changed.length} page${out.changed.length === 1 ? '' : 's'} — read it in the diff before committing.`,
        )
      }
    } catch (e) {
      toasts.error(
        'Could not tidy the redirects',
        because(
          e,
          `The redirects are unchanged. Open Redirects in Settings and fix the one it names.`,
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  async function commit(message: string) {
    if (!branch) return
    setBusy(true)
    try {
      const out = await api.commit(branch, message)
      toasts.success(
        `Committed ${out.sha.slice(0, 8)}`,
        `${out.paths.length} file${out.paths.length === 1 ? '' : 's'} — push to open a pull request.`,
      )
      await refresh()
      setDiff('')

      /**
       * Re-assess in the background.
       *
       * Content just changed, so the inbox is out of date — a link that was
       * broken may be fixed, and a page that was current may now point at
       * something that no longer exists. Not awaited, and deliberately: a scan
       * reads every page and asks git for each one's age, which is seconds of
       * work that nobody should be made to watch after a commit they already
       * know succeeded.
       *
       * A failure here is silent. The scheduled scan will pick it up, and
       * interrupting somebody to report that a background re-assessment did not
       * run is noise about a thing they did not ask for.
       */
      void api
        .scan(branch)
        .then(() => api.inbox(['open']))
        .then((body) => setInboxCount(body.counts['open'] ?? 0))
        .catch(() => undefined)
    } catch (e) {
      /**
       * Refused because somebody pushed to the repository, not because the
       * commit was wrong. There is a decision to take, per file, and a toast
       * is not a decision surface — so the report goes on screen and nothing
       * has been written either way.
       */
      if (e instanceof RequestFailed && e.code === 'branch_moved') {
        setMoved({ report: e.body['conflict'] as ConflictReport, message })
        return
      }
      toasts.error(
        'Commit refused',
        because(
          e,
          `Nothing was committed and your work is still here. Fix what it names, then try again.`,
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  /**
   * Apply the decisions, then commit again with the message already typed.
   *
   * Two requests rather than one, because they are two different acts: the
   * first writes nothing to git and only says which staged files survive, and
   * the second is an ordinary commit that can be refused by lint like any
   * other. Fusing them would hide a lint failure behind a conflict screen.
   */
  async function resolveMoved(choices: Record<string, 'mine' | 'theirs'>) {
    if (!branch || !moved) return
    const { report, message } = moved
    setBusy(true)
    try {
      const out = await api.resolveConflict(branch, report.actual, choices)
      setMoved(null)
      await refresh()
      if (out.staged === 0) {
        // A real outcome, not a failure: they took the branch's version of
        // everything, so there is nothing of theirs left to land. Committing
        // would answer "nothing staged to commit", which reads like a bug.
        setDiff('')
        toasts.success(
          'Their version stands',
          `Nothing of yours was left to commit. The draft now starts from ${report.actual.slice(0, 8)}.`,
        )
        return
      }
      await commit(message)
    } catch (e) {
      // It moved AGAIN — somebody pushed while this one was being read. The
      // decision was taken against a reading that is now out of date, so the
      // server refused it and sent a newer one.
      if (e instanceof RequestFailed && e.code === 'branch_moved') {
        setMoved({ report: e.body['conflict'] as ConflictReport, message })
        toasts.warn(
          'It moved again',
          'Somebody pushed while you were deciding. Nothing was written; here is what changed.',
        )
        return
      }
      toasts.error('Could not apply that', because(e, `Nothing changed. Try again.`))
    } finally {
      setBusy(false)
    }
  }

  /** Commit is a checkpoint. It deliberately does not merge or publish. */
  async function commitNow(note: string) {
    if (!branch) return
    const staged = state?.staged ?? []
    const message = note.trim() || `Commit ${staged.length} change${staged.length === 1 ? '' : 's'}`
    setBusy(true)
    try {
      if (staged.length > 0) {
        const out = await api.commit(branch, message)
        // The scan the drawer's own commit path runs, for the same reason.
        void api.scan(branch).catch(() => undefined)
        if (!out.sha) return
      }
    } catch (e) {
      if (e instanceof RequestFailed && e.code === 'branch_moved') {
        setBusy(false)
        setPublishOpen(false)
        setMoved({ report: e.body['conflict'] as ConflictReport, message })
        return
      }
      setBusy(false)
      toasts.error(
        'Not committed',
        because(e, `Your changes are still here. Fix what it names, then commit again.`),
      )
      return
    } finally {
      setBusy(false)
    }
    setPublishOpen(false)
    await refresh()
    setReviewOpen(true)
    toasts.success('Changes committed', 'Request a review, or merge this checkpoint into main.')
  }

  /**
   * Ask somebody to review a committed draft before it can merge.
   *
   * The same review thread the drawer opens — this is a second door to it,
   * from the commit sheet. A draft has to be committed for there to be
   * something stable to review, so this checkpoints staged work first.
   */
  async function sendForReview(note: string, reviewer: string | null) {
    if (!branch) return
    setBusy(true)
    try {
      if ((state?.staged.length ?? 0) > 0) {
        await api.commit(branch, note.trim() || 'Ready for review')
      }
      await api.requestReview(branch, note.trim() || undefined, reviewer ?? undefined)
      await refresh()
      setPublishOpen(false)
      toasts.success('Sent for review', 'They can approve it, or ask for changes.')
    } catch (e) {
      toasts.error(
        'Could not send it for review',
        because(e, `Your changes are still here. Try again, or merge it yourself if you may.`),
      )
    } finally {
      setBusy(false)
    }
  }

  async function mergeDraft() {
    if (!branch) return
    setBusy(true)
    try {
      const out = await api.merge(branch)
      toasts.success(
        'Merged',
        aimedAt
          ? `${branch} is now in ${aimedAt}. The live documentation is unchanged.`
          : `${branch} is now on ${out.branch}. Readers still see the previous publication until you publish main.`,
      )
      setBranch(null)
      await refresh(null)
      setReviewOpen(false)
      setDiff('')
      // Content changed on the published branch, so the inbox is out of date.
      void api
        .scan()
        .then(() => api.inbox(['open']))
        .then((body) => setInboxCount(body.counts['open'] ?? 0))
        .catch(() => undefined)
    } catch (e) {
      /**
       * Somebody edited the same page in GitHub, so this went to a pull
       * request instead of over the top of their words — §31.
       *
       * A warning and not an error, with the pull request as the action,
       * because nothing failed: the change is safe, it is in git, and it is
       * waiting for a person. Sticky, because it is a job rather than a fact —
       * a sentence that says "your publish became a pull request" disappearing
       * after four seconds is the product mentioning it.
       */
      if (e instanceof RequestFailed && e.code === 'proposed_pull_request') {
        const pr = e.body['pr'] as { number?: number; url?: string } | undefined
        toasts.warn(
          'Not merged — somebody else edited these pages',
          e.message,
          pr?.url
            ? { sticky: true, action: { label: `Open PR #${pr.number}`, href: pr.url } }
            : { sticky: true },
        )
        await refresh()
      } else if (e instanceof RequestFailed && e.code === 'merge_conflict') {
        /**
         * The live site and this draft changed the same lines of a file since
         * the draft began, and the server named the file. The way out is a
         * button rather than a sentence: the live version of each named file
         * onto the draft and the live site merged into it — one call, since
         * taking the file alone leaves the draft forked where it was and the
         * next publish refuses the same way — and a plain statement that the
         * change has to be made again.
         */
        const paths = Array.isArray(e.body['paths'])
          ? (e.body['paths'] as unknown[]).filter((p): p is string => typeof p === 'string')
          : []
        setReviewOpen(false)
        setMergeConflict(paths)
      } else {
        toasts.error(
          'Could not merge',
          because(
            e,
            `Nothing changed on main and your draft is still here. Resolve what it names and try again.`,
          ),
        )
      }
    } finally {
      setBusy(false)
    }
  }

  async function resolveMerge(resolutions: Record<string, 'draft' | 'main'>) {
    if (!branch) return
    setBusy(true)
    try {
      const result = await api.rebase(branch, resolutions)
      setMergeConflict(null)
      await refresh()
      setReviewOpen(true)
      toasts.success(
        'Draft rebased onto main',
        result.staged.length > 0
          ? 'Your chosen draft versions are staged. Review them, commit, and request review again.'
          : 'The draft now includes main. Its previous approval no longer applies; request review again.',
      )
    } catch (error) {
      toasts.error(
        'Could not rebase the draft',
        because(error, 'Nothing was discarded. Try again.'),
      )
    } finally {
      setBusy(false)
    }
  }

  async function publishMain() {
    setBusy(true)
    try {
      const out = await api.publish()
      await refresh(null)
      toasts.success(
        'Main published',
        `${out.sha.slice(0, 8)} is queued for deployment. Readers will see it when the build finishes.`,
      )
    } catch (error) {
      toasts.error('Could not publish main', because(error, 'Main is unchanged. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  async function push() {
    if (!branch) return
    setBusy(true)
    try {
      const pr = await api.push(branch)
      toasts.success('Pushed', 'A human reviews and merges the pull request.', {
        label: `Open PR #${pr.number}`,
        href: pr.url,
      })
      await refresh()
    } catch (e) {
      toasts.error(
        'Push failed',
        because(
          e,
          `Your work is committed and safe here. Try again, or check the repository connection in Settings.`,
        ),
      )
    } finally {
      setBusy(false)
    }
  }
}

// ---------------------------------------------------------------- bits

function SaveBadge({
  state,
  readOnly,
  viewer,
}: {
  state: SaveState
  readOnly: boolean
  viewer?: boolean
}) {
  /**
   * A viewer is told why, once, where the save state would be.
   *
   * The other read-only reasons are visible from the screen — no branch chosen,
   * or a branch marked merged in the picker beside this. "You have view access"
   * is not: without it the editor is simply inert, which looks broken rather
   * than deliberate.
   */
  if (viewer) {
    return (
      <span className="savestate">
        <Icon name="eye" size={12} /> View only
        {/* The reason, in the text rather than in a `title`: a span takes no
            focus, so a tooltip on one is a sentence only a mouse can read. */}
        <span className="sr-only"> — ask an admin for editor access to make changes.</span>
      </span>
    )
  }
  if (readOnly) return null
  if (state === 'saving') {
    return (
      <span className="savestate savestate--saving">
        <Icon name="spinner" size={12} /> Saving
      </span>
    )
  }
  if (state === 'offline') {
    return (
      <span className="savestate savestate--dirty">
        <Icon name="alert" size={12} /> Offline — kept on this device
        <span className="sr-only">
          . No connection. Your words are kept in this browser and will be saved the moment it is
          back.
        </span>
      </span>
    )
  }
  if (state === 'dirty') {
    return (
      <span className="savestate savestate--dirty">
        <span className="dot dot--move" /> Unsaved
      </span>
    )
  }
  if (state === 'error') {
    /**
     * §66: what happened, whether the work is safe, what to do next.
     *
     * "Save failed" answered only the first, and the missing half is the half
     * somebody panics about. The edit IS safe — it is on screen and mirrored
     * into this browser on every keystroke — and the next keystroke retries,
     * so the honest instruction is "keep typing", not "copy your work out".
     */
    return (
      <span className="savestate savestate--dirty">
        <Icon name="alert" size={12} /> Not saved — kept here, retrying
        <span className="sr-only">
          . Your edit is kept in this browser and the next change tries again.
        </span>
      </span>
    )
  }
  if (state === 'conflict') {
    // Says whose problem it is. "Save failed" would read as a bug in the
    // product, and the work is not lost — it is on screen, waiting.
    return (
      <span className="savestate savestate--dirty">
        <Icon name="alert" size={12} /> Not saved — changed elsewhere
        <span className="sr-only">. Somebody else saved this page.</span>
      </span>
    )
  }
  return (
    <span className="savestate savestate--saved">
      <Icon name="check" size={12} /> Saved
    </span>
  )
}

/**
 * Sign in, or create an account.
 *
 * One form with a toggle rather than two screens. Sign-up asks for four things at
 * once — organisation, name, email, password — because a workspace is required to
 * do anything at all, and deferring it drops a new account into a state where
 * nothing is actionable.
 *
 * There is no token field any more. The session is an httpOnly cookie the server
 * sets, so this component never touches a credential after the form is submitted.
 */
function Gate({
  onSignedIn,
  movedTo,
  sessionEnded,
  invitation,
}: {
  /**
   * `signedUp` is true only when this session was just created, not signed
   * into; `spent` is true when the invitation went with it, so the caller
   * knows not to spend the same token a second time.
   */
  onSignedIn: (
    /**
     * The session, with what the code step alone can say about it: whether a
     * RECOVERY code was spent to make it, and how many are left. Optional
     * because the other four doors into this callback know neither.
     */
    session: Session & { usedRecoveryCode?: boolean; recoveryCodesLeft?: number | null },
    signedUp: boolean,
    spent: boolean,
  ) => void
  /**
   * The address this account has just moved to, when somebody arrived here by
   * confirming one. Without it they are looking at a sign-in form that has
   * apparently forgotten them, holding an address that no longer works.
   */
  movedTo?: string | null
  /**
   * True when the session ended under somebody who was mid-edit, rather than
   * this being an ordinary first visit. The difference is worth a sentence:
   * one of these two people has unsaved words and no idea where they went.
   */
  sessionEnded?: boolean
  /**
   * The invitation token they arrived on, if they did.
   *
   * Without it this screen showed a stranger a blank form with an
   * "Organisation name" field, which is the one question somebody joining an
   * existing organisation cannot answer — and they had to answer it, own the
   * empty workspace it made, and only then find out whether the invitation
   * was for the address they typed.
   */
  invitation?: string | null
}) {
  const [mode, setMode] = useState<'in' | 'up'>(invitation ? 'up' : 'in')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  /**
   * The receipt for a correct password, when the account has a second factor.
   *
   * Its presence IS the second step: while it is here the password fields are
   * gone and one field is on screen. It is deliberately not persisted
   * anywhere — a reload starts the sign-in again, which is the right outcome
   * for a five-minute proof of somebody's password.
   */
  const [challenge, setChallenge] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [organisation, setOrganisation] = useState('')
  const [error, setError] = useState<string | null>(null)
  /** Something that went RIGHT, said on the screen it ends on. */
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /**
   * Whether Google is a door here. False until the server says otherwise, so a
   * button that cannot work never flashes — an installation without Google
   * configured shows exactly the form it always showed.
   */
  /**
   * The provider doors this deployment actually has.
   *
   * Asked for rather than assumed: the person at the gate has no session, and
   * a button that leads to a route nobody mounted is worse than no button.
   */
  const [doors, setDoors] = useState<{ id: 'google' | 'github'; label: string }[]>([])

  /**
   * Coming back from Google as somebody new.
   *
   * The callback cannot finish a sign-up on its own: Google says who somebody
   * is and cannot say what to call their workspace, so it carries the claims
   * back in a signed cookie and sends the browser to `/welcome`. There was no
   * `/welcome` — the door opened onto the sign-in form, and a first Google
   * sign-in could not be completed at all. This is the screen it meant.
   */
  const [welcoming, setWelcoming] = useState(() => window.location.pathname === '/welcome')
  /**
   * Which provider sent us here.
   *
   * The claims wait in a cookie named for the provider, so finishing against
   * the wrong door finds no claims and asks the person to start again for no
   * reason they can see. Absent means Google, which is what every link written
   * before there was a second provider means.
   */
  const [welcomeProvider] = useState<'google' | 'github'>(() =>
    new URLSearchParams(window.location.search).get('provider') === 'github' ? 'github' : 'google',
  )

  /**
   * Forgetting a password, and the link that answers it.
   *
   * Both routes existed and neither had a screen, so a forgotten password was
   * a locked door with no way through it: the only recovery was somebody with
   * database access. `resetting` holds the token from `/reset?token=…`, taken
   * out of the address bar on arrival like every other capability in a URL.
   */
  const [forgetting, setForgetting] = useState(false)
  const [asked, setAsked] = useState(false)

  /**
   * Signing in with a link instead of a password.
   *
   * `linkSent` is the receipt for asking; `spending` is the moment after
   * opening one, when this screen is doing something and must say so rather
   * than sitting on the sign-in form as though nothing had happened.
   */
  const [linkSent, setLinkSent] = useState(false)
  const [linkToken] = useState<string | null>(() => {
    if (window.location.pathname !== '/link') return null
    const found = new URLSearchParams(window.location.search).get('token')
    if (found) window.history.replaceState({}, '', '/')
    return found
  })
  const [spending, setSpending] = useState(linkToken !== null)
  const [resetting, setResetting] = useState<string | null>(() => {
    if (window.location.pathname !== '/reset') return null
    const found = new URLSearchParams(window.location.search).get('token')
    if (found) window.history.replaceState({}, '', '/')
    return found
  })

  /**
   * And what the callback says when it could not get that far. It redirects to
   * `/signin?error=…` on a forged or expired attempt, which nothing read, so
   * the person landed on a sign-in form that looked like it had simply
   * forgotten them.
   */
  /**
   * A provider sign-in that still owes a second factor.
   *
   * The callback cannot finish it: an account that enrolled an authenticator
   * is asked for a code whichever door it came through, so the browser comes
   * back here with `?second_factor=1` and the receipt in a cookie. Picking it
   * up puts this screen into exactly the state a correct password leaves it
   * in — one field, and the same route spends it.
   */
  useEffect(() => {
    const asked = new URLSearchParams(window.location.search)
    if (asked.get('second_factor') !== '1') return
    // The receipt is a cookie named for the provider, so the callback says
    // which one it was. Older links carry no name and mean Google.
    const provider = asked.get('provider') === 'github' ? 'github' : 'google'
    window.history.replaceState({}, '', '/')
    void api
      .oauthSecondFactor(provider)
      .then((pending) => {
        if (pending?.challenge) setChallenge(pending.challenge)
        else setError('That sign-in took too long. Try again.')
      })
      .catch(() => setError('That sign-in took too long. Try again.'))
  }, [])

  /**
   * Arriving on a sign-in link.
   *
   * The token comes out of the address bar on arrival, like every other
   * capability in a URL here, and is spent immediately — there is nothing to
   * ask the person, which is the point of the link. An enrolled account still
   * owes a code, and the answer says so in exactly the shape a correct
   * password produces, so this lands on the same one-field screen.
   */
  useEffect(() => {
    if (!linkToken) return
    void api
      .signInWithLink(linkToken)
      .then((result) => {
        if (needsCode(result)) {
          setChallenge(result.challenge)
          setCode('')
          setSpending(false)
          return
        }
        onSignedIn(result, false, false)
      })
      .catch((e) => {
        setSpending(false)
        setError(e instanceof Error ? e.message : 'That link is no longer valid.')
      })
    // The token is spent once, on arrival; re-running would spend a link that
    // is already gone and report it as expired.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken])

  useEffect(() => {
    const why = new URLSearchParams(window.location.search).get('error')
    if (!why) return
    window.history.replaceState({}, '', '/')
    setError(
      why === 'oauth_state'
        ? 'That sign-in took too long, or did not start here. Try again.'
        : 'That sign-in did not finish. Try again.',
    )
  }, [])
  useEffect(() => {
    void api
      .providers()
      .then((p) =>
        setDoors(
          [
            { id: 'google' as const, label: 'Google', on: p.google },
            { id: 'github' as const, label: 'GitHub', on: p.github },
          ]
            .filter((door) => door.on)
            .map(({ id, label }) => ({ id, label })),
        ),
      )
      .catch(() => setDoors([]))
  }, [])

  /**
   * Who invited them, read from the token before anybody signs in.
   *
   * `null` while it is in flight, so nothing about an invitation flashes on
   * screen for somebody who arrived here without one. A token that is no
   * longer live is not an error to hide: "you have already joined" is the most
   * likely reason somebody clicks the same link twice, and it has an action.
   */
  const [invited, setInvited] = useState<{
    workspace: string
    email: string
    /* What they are being invited AS. The lookup has always returned it; the
       screen simply never said it, so somebody accepted without being told
       whether they could write, publish or only read. */
    role: Role
    canPublish: boolean
    canReview?: boolean
    canManageBilling?: boolean
    state: 'pending' | 'accepted' | 'revoked' | 'expired'
    /* Whether the workspace asks for a second step. Said on this screen
       because being asked for an authenticator app a minute AFTER accepting
       an invitation is a bad first minute. */
    requireTwoFactor?: boolean
  } | null>(null)
  const [invitationProblem, setInvitationProblem] = useState<string | null>(null)
  useEffect(() => {
    if (!invitation) return
    let cancelled = false
    void api
      .describeInvitation(invitation)
      .then(({ invitation: found }) => {
        if (cancelled) return
        setInvited(found)
        if (found.state === 'pending') {
          // The address it was written for, filled in rather than described:
          // the check is on the server and getting it wrong costs them the
          // whole form. Still editable — somebody whose account is under
          // another address needs to be able to say so and sign in instead.
          setEmail(found.email)
          return
        }
        setMode('in')
        setInvitationProblem(
          found.state === 'accepted'
            ? `You have already joined ${found.workspace}. Sign in below.`
            : found.state === 'revoked'
              ? `That invitation to ${found.workspace} was withdrawn. Ask them for a new one.`
              : `That invitation to ${found.workspace} has expired. Ask them for a new one.`,
        )
      })
      .catch(() => {
        if (cancelled) return
        setMode('in')
        setInvitationProblem('That invitation link is not valid. Ask them for a new one.')
      })
    return () => {
      cancelled = true
    }
  }, [invitation])

  /** Live, and therefore worth changing the form for. */
  const joining = invited?.state === 'pending' ? invited : null

  /**
   * What is stopping this from being submitted, if anything.
   *
   * The button used to be disabled until every field had something in it, which
   * BRAND.md 13 forbids for a reason: a control that is inert and silent leaves
   * the reader to work out which of four fields it is waiting for. Being
   * `disabled` also takes it out of the tab order, so a keyboard user cannot
   * even reach the thing that is refusing them.
   *
   * So it stays clickable and answers when clicked.
   */
  const missing = (): string | null => {
    // Not asked for when they are joining somebody else's workspace: there is
    // no organisation for them to name, and the field is not on the screen.
    if (mode === 'up' && !invitation && !organisation.trim())
      return 'Name the organisation this workspace is for.'
    if (mode === 'up' && !displayName.trim())
      return 'Enter your name, so your edits are attributable.'
    if (!email.trim()) return 'Enter your email address.'
    if (!password) return mode === 'in' ? 'Enter your password.' : 'Choose a password.'
    return null
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const stopped = missing()
    if (stopped) {
      setError(stopped)
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (mode === 'up') {
        onSignedIn(
          await api.signUp({
            email,
            password,
            displayName,
            ...(invitation ? { invitation } : { organisationName: organisation }),
          }),
          true,
          Boolean(invitation),
        )
        return
      }
      const result = await api.signIn(email, password)
      /**
       * A correct password, and no session. Nothing here is signed in yet:
       * what came back is a challenge, and the code step is what spends it.
       */
      if (needsCode(result)) {
        setChallenge(result.challenge)
        setCode('')
        return
      }
      onSignedIn(result, false, false)
    } catch (e) {
      // The server's message, not one invented here: it distinguishes a wrong
      // password from a taken email from a short password, and each needs a
      // different action from the person reading it.
      //
      // One exception. `invalid_credentials` is deliberately the SAME sentence
      // for an unknown email and a wrong password — the server must not let
      // this form be used to test which addresses have accounts — but the bare
      // sentence answers only "what happened". It says nothing about what to
      // do next, so that half is added here rather than on the server, which
      // has no business knowing this is a form with a mode toggle two inches
      // below it.
      if (e instanceof RequestFailed && e.code === 'invalid_credentials') {
        setError(
          `${e.message} Check both and try again, or create a workspace if you are new here.`,
        )
      } else {
        setError(e instanceof Error ? e.message : 'Could not sign in.')
      }
    } finally {
      setBusy(false)
    }
  }

  /** Ask for the link. The answer is the same either way, deliberately. */
  const submitForgot = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!email.trim()) {
      setError('Enter the address you sign in with.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.forgotPassword(email.trim())
      setAsked(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not ask for a link.')
    } finally {
      setBusy(false)
    }
  }

  /** Ask for a sign-in link. Same silence as the reset, same wording. */
  const submitLink = async () => {
    if (!email.trim()) {
      setError('Enter the address you sign in with.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.requestSignInLink(email.trim())
      setLinkSent(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not ask for a link.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Spend it. The server revokes every session, including this browser's, so
   * this ends at the sign-in form rather than inside the product — with the
   * new password, which is the thing that was just proven.
   */
  const submitReset = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!password) {
      setError('Choose a password.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.resetPassword(resetting ?? '', password)
      setResetting(null)
      setForgetting(false)
      setAsked(false)
      setPassword('')
      setMode('in')
      setNote('Password changed. Sign in with it.')
    } catch (e) {
      // The server distinguishes a spent link from a password that is too
      // short, and the two need different things from the person reading.
      setError(e instanceof Error ? e.message : 'Could not set that password.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Finish the Google sign-up: one field, because everything else came from
   * Google. On success there IS a session — the server set the cookie — so who
   * we are is read back rather than guessed at from the reply.
   */
  const submitWelcome = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!organisation.trim()) {
      setError('Name the organisation this workspace is for.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.finishOAuth(organisation.trim(), welcomeProvider)
      window.history.replaceState({}, '', '/')
      setWelcoming(false)
      onSignedIn(await api.me(), true, false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not finish signing in.')
    } finally {
      setBusy(false)
    }
  }

  const submitCode = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!code.trim()) {
      setError('Enter the code from your authenticator, or one of your recovery codes.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const session = await api.signInWithCode(challenge!, code.trim())
      /**
       * A spent recovery code is worth saying out loud, and the session says
       * it — so the sentence is carried through rather than drawn here.
       *
       * It used to be a `window.alert` on a zero-delay timer, which is the
       * operating system stopping the sign-in to be dismissed before anything
       * could be read behind it: this screen is about to be replaced by the
       * application, and the one thing the sentence is FOR — "set up two-step
       * sign-in again" — is a place in that application it could not offer to
       * open. The shell says it, where the rest of what just happened is said.
       */
      onSignedIn(session, false, false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not check that code.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * The second step, on its own screen rather than a field grafted under the
   * password. There is nothing else to do here, and leaving the password
   * fields on screen invites somebody to retype them at a form that is no
   * longer asking.
   */
  if (spending) {
    return (
      <div className="gate">
        <div className="gate__identity">
          <span className="gate__mark">
            <Icon name="logo" size={26} />
          </span>
          <h1>Staleless</h1>
          <p className="gate__tagline">Docs should never be stale.</p>
        </div>
        <GateShowcase />

        <div className="gate__card">
          <p className="gate__text" data-link-spending>
            Signing you in…
          </p>
        </div>
      </div>
    )
  }

  if (linkSent) {
    return (
      <div className="gate">
        <div className="gate__identity">
          <span className="gate__mark">
            <Icon name="logo" size={26} />
          </span>
          <h1>Staleless</h1>
          <p className="gate__tagline">Docs should never be stale.</p>
        </div>
        <GateShowcase />

        <div className="gate__card">
          {/*
            The same careful sentence the reset uses: never "we sent you an
            email". The answer is identical whether or not that address has an
            account, and a deployment with no mail provider logs the link
            instead of sending it.
          */}
          <p className="gate__text" data-link-said>
            If that address has an account, a sign-in link is on its way. It works once, and for
            fifteen minutes.
          </p>
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            onClick={() => {
              setLinkSent(false)
              setError(null)
            }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    )
  }

  if (resetting) {
    return (
      <div className="gate">
        <div className="gate__identity">
          <span className="gate__mark">
            <Icon name="logo" size={26} />
          </span>
          <h1>Staleless</h1>
          <p className="gate__tagline">Docs should never be stale.</p>
        </div>
        <GateShowcase />

        <div className="gate__card">
          <p className="gate__text">Choose a new password.</p>
          <form className="gate__form gate__form--stacked" onSubmit={submitReset}>
            <input
              autoFocus
              className="input"
              type="password"
              autoComplete="new-password"
              data-reset-password
              value={password}
              placeholder="Password"
              onChange={(e) => setPassword(e.target.value)}
            />
            {/* Under the field, not inside it — the same reason as on sign-up.
                A placeholder is gone the moment somebody types, which is
                exactly when a length rule starts to matter. */}
            <p className="gate__hint">At least 12 characters.</p>
            {error && (
              <p className="gate__error" role="alert">
                {error}
              </p>
            )}
            <button className="btn btn--primary" disabled={busy} type="submit">
              {busy ? 'One moment…' : 'Set password'}
            </button>
          </form>
          <p className="gate__footnote">
            Everywhere you are signed in will be signed out, including here — a password nobody can
            prove they know is not a session.
          </p>
        </div>
      </div>
    )
  }

  if (forgetting) {
    return (
      <div className="gate">
        <div className="gate__identity">
          <span className="gate__mark">
            <Icon name="logo" size={26} />
          </span>
          <h1>Staleless</h1>
          <p className="gate__tagline">Docs should never be stale.</p>
        </div>
        <GateShowcase />

        <div className="gate__card">
          {asked ? (
            <>
              {/*
                Careful not to say an email arrived: the answer is the same
                whether or not that address has an account — the form must not
                become a way to find out who does — and a deployment with no
                mail provider logs the link instead of sending it.
              */}
              <p className="gate__text" data-forgot-said>
                If that address has an account, a link is on its way. It works once, and for an
                hour.
              </p>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => {
                  setForgetting(false)
                  setAsked(false)
                  setError(null)
                }}
              >
                Back to sign in
              </button>
            </>
          ) : (
            <>
              {/*
                A heading, a real label, and what the link will do — all three
                were missing. The screen opened on one grey sentence and an
                unlabelled box: a person arriving here is already anxious, and
                the box could only be identified by the word inside it, which
                disappears the moment they type. The reassurance that a reset
                link is single-use and short-lived belonged BEFORE the press,
                not only in the confirmation afterwards.
              */}
              <h1 className="gate__title">Reset your password</h1>
              <p className="gate__text">
                Tell us the address you sign in with and we will send a link. It works once, and for
                an hour.
              </p>
              <form className="gate__form gate__form--stacked" onSubmit={submitForgot}>
                <label className="field">
                  <span className="label">Email address</span>
                  <input
                    autoFocus
                    className="input"
                    type="email"
                    autoComplete="email"
                    data-forgot-email
                    value={email}
                    placeholder="you@example.com"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                {error && (
                  <p className="gate__error" role="alert">
                    {error}
                  </p>
                )}
                <button className="btn btn--primary" disabled={busy} type="submit">
                  {busy ? 'One moment…' : 'Send a link'}
                </button>
              </form>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => {
                  setForgetting(false)
                  setError(null)
                }}
              >
                Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  if (welcoming) {
    return (
      <div className="gate">
        <div className="gate__identity">
          <span className="gate__mark">
            <Icon name="logo" size={26} />
          </span>
          <h1>Staleless</h1>
          <p className="gate__tagline">Docs should never be stale.</p>
        </div>
        <GateShowcase />

        <div className="gate__card">
          <p className="gate__text">
            {/* Named, because this screen is reached from GitHub too and
                telling somebody Google said something it did not is a small
                lie on the first screen of their account. */}
            {welcomeProvider === 'github' ? 'GitHub' : 'Google'} told us who you are. It cannot tell
            us what to call your workspace.
          </p>
          <form className="gate__form gate__form--stacked" onSubmit={submitWelcome}>
            <input
              autoFocus
              className="input"
              data-welcome-organisation
              value={organisation}
              /* The sentence above this field says "workspace", and so does
                 the button below it. The field said "Organisation". */
              placeholder="Workspace name"
              onChange={(e) => setOrganisation(e.target.value)}
            />
            {error && (
              <p className="gate__error" role="alert">
                {error}
              </p>
            )}
            <button className="btn btn--primary" disabled={busy} type="submit">
              {busy ? 'One moment…' : 'Create workspace'}
            </button>
          </form>
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            onClick={() => {
              window.history.replaceState({}, '', '/')
              setWelcoming(false)
              setError(null)
            }}
          >
            Start again
          </button>
        </div>
      </div>
    )
  }

  if (challenge) {
    return (
      <div className="gate">
        <div className="gate__identity">
          <span className="gate__mark">
            <Icon name="logo" size={26} />
          </span>
          <h1>Staleless</h1>
          <p className="gate__tagline">Docs should never be stale.</p>
        </div>
        <GateShowcase />

        <div className="gate__card">
          <p className="gate__text">
            Your password was right. Now the six digits from your authenticator.
          </p>
          <form className="gate__form gate__form--stacked" onSubmit={submitCode}>
            <input
              autoFocus
              className="input"
              // No numeric input mode: the same field takes a recovery code,
              // and a keyboard with no letters on it is a trap for the person
              // who has lost their phone and is already having a bad day.
              autoComplete="one-time-code"
              data-totp-code
              value={code}
              placeholder="Code, or a recovery code"
              onChange={(e) => setCode(e.target.value)}
            />
            {error && (
              <p className="gate__error" role="alert">
                {error}
              </p>
            )}
            <button className="btn btn--primary" disabled={busy} type="submit">
              {busy ? 'One moment…' : 'Confirm'}
            </button>
          </form>
          <p className="gate__footnote">
            Lost the authenticator? Type one of the recovery codes you saved. Each works once.
          </p>
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            onClick={() => {
              setChallenge(null)
              setCode('')
              setError(null)
            }}
          >
            Start again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="gate">
      <div className="gate__identity">
        <span className="gate__mark">
          <Icon name="logo" size={26} />
        </span>
        <h1>Staleless</h1>
        <p className="gate__tagline">Docs should never be stale.</p>
      </div>
      <GateShowcase />

      <div className="gate__card">
        {/*
          Somebody who just moved their address arrives here signed out, and
          the address they know no longer works. Told, with the new one, rather
          than left at a form that has apparently forgotten them.
        */}
        {/*
          The session ended with words on the screen. Says what happened, that
          the words are safe, and what signing in will do with them — the three
          things somebody in that position is asking, in that order.
        */}
        {sessionEnded && (
          <p className="gate__text gate__text--warn" data-session-ended>
            Your session ended while you were writing. This browser kept that edit — sign in and the
            page offers it back.
          </p>
        )}
        {movedTo && (
          <p className="gate__text" data-moved-to>
            This account signs in with <b>{movedTo}</b> now. Use it below.
          </p>
        )}
        {/*
          Somebody arriving on an invitation. Said before the form, because it
          is the answer to "what is this and why am I here" — and because the
          form below is a different form for them: no organisation to name, and
          the address already filled in.
        */}
        {joining && (
          <p className="gate__text" data-invited-to={joining.workspace}>
            <b>{joining.workspace}</b> invited you to their documentation
            {/*
              AS WHAT. The screen named the workspace and the address and left
              out the one thing somebody accepting is agreeing to — whether
              they are about to be able to write, publish, or only read. The
              answer was already on the client, in the invitation the link
              looks up; it was simply never said.
            */}
            {' as '}
            <b data-invited-as={joining.role}>{ROLE_LABELS[joining.role].split(' — ')[0]}</b> —{' '}
            {ROLE_LABELS[joining.role].split(' — ')[1]}
            {[
              joining.canPublish ? 'publish' : null,
              joining.canReview ? 'review' : null,
              joining.canManageBilling ? 'billing' : null,
            ].some(Boolean)
              ? `, with ${[
                  joining.canPublish ? 'publish' : null,
                  joining.canReview ? 'review' : null,
                  joining.canManageBilling ? 'billing' : null,
                ]
                  .filter(Boolean)
                  .join(', ')} access`
              : joining.role === 'editor'
                ? ', and somebody else decides what publishes'
                : ''}
            . Create your account with <b>{joining.email}</b>, or sign in if you already have one.
          </p>
        )}
        {joining?.requireTwoFactor && (
          /*
           * Said here rather than a minute later. This workspace asks for a
           * code from an authenticator as well as a password, and finding
           * that out immediately after accepting an invitation — with an
           * account already made — is the bad first minute this sentence
           * exists to prevent. Have the phone ready, not go and find it.
           */
          <p className="gate__text" data-invited-two-factor>
            {joining.workspace} asks everybody for two-step sign-in. Have an authenticator app on
            your phone ready — you will set it up right after you join.
          </p>
        )}
        {invitationProblem && (
          <p className="gate__text gate__text--warn" data-invitation-problem>
            {invitationProblem}
          </p>
        )}
        {/*
          The task, as the heading. The wordmark above is the product's name
          and answers "where am I"; this answers "what am I doing here", and
          they are different questions — running them together as one line of
          body text left the screen with no heading at all above the fold.
          h2 rather than h1: the lockup owns the h1, and a second one would
          leave a screen reader with two competing page titles.
        */}
        <h2 className="gate__title">
          {mode === 'in'
            ? 'Sign in'
            : joining
              ? `Join ${joining.workspace}`
              : 'Create your workspace'}
        </h2>
        {/*
          A sentence, not half of one. "Your documentation, and the date every
          page was last true" is a noun phrase with no verb in it: it reads as
          a caption that lost its picture, on the screen where somebody is
          deciding whether this product is worth an account. What replaces it
          names the three states the illustration beside it is showing, in the
          order it shows them.
        */}
        <p className="gate__text">
          {mode === 'in'
            ? 'Pick up where your documentation left off.'
            : joining
              ? 'Create your account, and start writing with them.'
              : "Know what's current, what needs review, and what changed."}
        </p>

        <form className="gate__form gate__form--stacked" onSubmit={submit}>
          {doors.length > 0 && (
            <>
              {doors.map((door) => (
                <a
                  key={door.id}
                  className="btn btn--block"
                  /*
                    The invitation goes WITH them. Pressing this leaves the
                    origin entirely and comes back on a fresh page load, so a
                    token held in memory here is gone by the time they return —
                    and an invited person who used a provider door got the old
                    behaviour: asked to name an organisation, given a workspace
                    nobody wanted. The server puts it in the signed cookie it
                    already carries the state in.
                  */
                  href={`/auth/${door.id}${
                    invitation ? `?invitation=${encodeURIComponent(invitation)}` : ''
                  }`}
                >
                  {/* The mark, where there is one. A provider door is scanned
                      rather than read — somebody is looking for the logo of
                      the account they already have — and GitHub's is the one
                      third-party mark this icon set carries. Google's is four
                      colours and cannot be drawn in `currentColor`, so that
                      door stays a label until there is an honest way to draw
                      it; a monochrome Google G is not their mark either. */}
                  {door.id === 'github' && <Icon name="github" size={17} />}
                  Continue with {door.label}
                </a>
              ))}
              <div className="gate__divider">
                <span>or</span>
              </div>
            </>
          )}
          {/*
            A name on every field, not a placeholder.

            A placeholder leaves the moment somebody types, so the question a
            half-filled box is answering disappears exactly when they look up
            to check it — and on the one screen where a person is typing their
            own address and inventing a password, "Email" and "Password" were
            written where they cannot survive being answered. The same rule the
            rest of the product now follows.
          */}
          {mode === 'up' && (
            <>
              {!joining && (
                <label className="field">
                  {/* "Workspace name", because the flow says workspace: the
                      heading above is "Create your workspace" and the button
                      below is "Create workspace". Asking for an ORGANISATION
                      in between asks a different question — somebody's
                      organisation is Asynk Tech and their workspace is API
                      Docs — and the field only ever named the workspace. */}
                  <span className="label">Workspace name</span>
                  <input
                    autoFocus
                    className="input"
                    value={organisation}
                    placeholder="Acme Docs"
                    onChange={(e) => setOrganisation(e.target.value)}
                  />
                </label>
              )}
              <label className="field">
                <span className="label">Your name</span>
                <input
                  // The first field on the screen when there is no organisation
                  // to name, so the caret is never left nowhere.
                  autoFocus={Boolean(joining)}
                  className="input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </label>
            </>
          )}
          <label className="field">
            <span className="label">Email address</span>
            <input
              autoFocus={mode === 'in'}
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              placeholder="you@example.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="label">Password</span>
            <input
              className="input"
              type="password"
              // Tells a password manager which field this is, and whether to
              // offer a new password or an existing one.
              autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {/*
            The rule, under the field rather than inside it. A placeholder is
            gone the moment somebody types, which is exactly when a length
            requirement starts to matter — so the one piece of text that has
            to survive typing was the one piece written where it cannot.
          */}
          {mode === 'up' && <p className="gate__hint">At least 12 characters.</p>}

          {error && (
            <p className="gate__error" role="alert">
              {error}
            </p>
          )}
          {note && !error && (
            <p className="gate__text" role="status" data-gate-note>
              {note}
            </p>
          )}

          {/* Disabled only while a request is in flight, which is the one case
              where the reason is obvious and stated on the button itself. */}
          <button className="btn btn--primary" disabled={busy} type="submit">
            {busy ? 'One moment…' : mode === 'in' ? 'Sign in' : 'Create workspace'}
          </button>
        </form>

        {mode === 'in' && (
          /*
            Two ways out of "I cannot get in", and they answer different
            questions. A reset is for somebody who wants a password again; a
            sign-in link is for somebody who never had one — the colleague who
            accepted an invitation, or an account that has only ever arrived
            through Google.

            Drawn as LINKS, which is what they are. They were `.btn--ghost
            .btn--sm`: a borderless, fill-less, grey control, which on a screen
            with nothing else in that grey is indistinguishable from a caption.
            Both were read as notes about the screen rather than things to
            press. A button that navigates within the app still gets the app's
            link treatment — the accent text colour and an underline — because
            "can I press this" is a question about how it looks, not about
            which element it is.
          */
          <p className="gate__aside">
            <button
              className="gate__link"
              type="button"
              data-link-ask
              onClick={() => void submitLink()}
            >
              Email me a sign-in link
            </button>
            <button
              className="gate__link"
              type="button"
              data-forgot
              onClick={() => {
                setForgetting(true)
                setError(null)
                setNote(null)
              }}
            >
              Forgot your password?
            </button>
          </p>
        )}

        {/*
          The other thing somebody might be here to do, and the way back from
          it. Both used to be wrong, in opposite directions.

          "Create a workspace" was `.btn--tinted` — a filled block in the
          accent wash. On a screen that has just taught the eye that lime means
          "press this", a murky filled rectangle that is ALMOST lime reads as
          the lime one, greyed out: a disabled button, on the action most
          people who land here actually want. It is now an outline button,
          which is the system's other answer to "important, but not THE
          action": full width and bordered, so it is unmistakably a control,
          with no fill to be mistaken for a broken primary.

          Going back the other way was a line of grey text saying "I already
          have an account", which on the sign-up screen could be missed
          altogether. It is now a question with a link in it, which is how
          every product on the internet says this, for the good reason that it
          works.
        */}
        {mode === 'in' ? (
          <div className="gate__new">
            <span className="gate__new-lead">New to Staleless?</span>
            <button
              className="btn btn--outline btn--block"
              data-create-workspace
              onClick={() => {
                setMode('up')
                setError(null)
              }}
              type="button"
            >
              {/* Not "Create a workspace" for somebody joining one that
                  already exists — the button would promise the very thing
                  this screen exists to stop them doing by accident. */}
              {joining ? 'Create an account' : 'Create a workspace'}
              <span className="gate__arrow" aria-hidden="true">
                →
              </span>
            </button>
          </div>
        ) : (
          <p className="gate__back">
            Already have an account?{' '}
            <button
              className="gate__link"
              data-have-account
              onClick={() => {
                setMode('in')
                setError(null)
              }}
              type="button"
            >
              Sign in
              <span className="gate__arrow" aria-hidden="true">
                →
              </span>
            </button>
          </p>
        )}
      </div>

      {/*
        The positioning line, given somewhere to be.

        It was a lone grey sentence in the bottom-left corner of an otherwise
        empty half-screen, a foot below anything it could be about, and it read
        as a copyright notice somebody forgot to finish. Paired with the domain
        and aligned to the same column as the form, it is a brand block: the
        thing a footer is, rather than a caption that drifted.
      */}
      <div className="gate__brand">
        <p className="gate__brand-line">Documentation that keeps up.</p>
        <p className="gate__brand-site">staleless.com</p>
      </div>
    </div>
  )
}

function Boot({
  error,
  justSignedUp,
  onRetry,
  onReset,
  onCreateWorkspace,
}: {
  error: BootError | null
  justSignedUp: boolean
  onRetry: () => void
  onReset: () => void
  /** Only reachable from the no-workspace screen; see the branch below. */
  onCreateWorkspace: (name: string) => Promise<void>
}) {
  const [organisation, setOrganisation] = useState('')
  const [creating, setCreating] = useState<{ busy: boolean; error: string | null }>({
    busy: false,
    error: null,
  })
  const create = async (name: string) => {
    if (!name) {
      setCreating({ busy: false, error: 'Name the organisation this workspace is for.' })
      return
    }
    setCreating({ busy: true, error: null })
    try {
      await onCreateWorkspace(name)
    } catch (e) {
      setCreating({ busy: false, error: e instanceof Error ? e.message : String(e) })
    }
  }
  if (!error) {
    return (
      <div className="app app--boot" aria-busy="true">
        {/* Match the real shell's two columns. The previous loader put `.top`
            and `.body` directly in `.app`, so CSS treated the top bar as the
            sidebar and vertically centred its status halfway down the page. */}
        <aside className="sidenav bootnav" aria-hidden="true">
          <div className="bootnav__head">
            <span className="skeleton boot__mark" />
            <span className="bootnav__names">
              <span className="skeleton boot__line boot__line--workspace" />
              <span className="skeleton boot__line boot__line--site" />
            </span>
          </div>
          <span className="skeleton bootnav__search" />
          <div className="bootnav__places">
            {[72, 88, 68].map((width, i) => (
              <span key={i} className="bootnav__row">
                <span className="skeleton boot__glyph" />
                <span className="skeleton boot__line" style={{ width }} />
              </span>
            ))}
          </div>
          <span className="skeleton bootnav__tabs" />
          <div className="bootnav__tree">
            {[76, 62, 84, 70, 55].map((width, i) => (
              <span key={i} className="bootnav__row">
                <span className="skeleton boot__glyph" />
                <span className="skeleton boot__line" style={{ width: `${width}%` }} />
              </span>
            ))}
          </div>
          <div className="bootnav__foot">
            {[68, 82, 60].map((width, i) => (
              <span key={i} className="bootnav__row">
                <span className="skeleton boot__glyph" />
                <span className="skeleton boot__line" style={{ width }} />
              </span>
            ))}
          </div>
        </aside>

        <div className="appmain">
          <div className="topstack">
            <header className="top boot__top">
              <span className="skeleton boot__crumb" aria-hidden="true" />
              <span className="top__spacer" />
              {/* Real words distinguish a slow repository from a broken app. */}
              <span className="boot__status" role="status">
                <Icon name="spinner" size={12} />
                {justSignedUp ? 'Setting up your workspace…' : 'Loading your workspace…'}
              </span>
              <span className="skeleton boot__top-action" aria-hidden="true" />
            </header>
          </div>

          <div className="body" aria-hidden="true">
            <main className="main">
              <div className="viewbar boot__viewbar">
                <span className="skeleton boot__tabs" />
                <span className="top__spacer" />
                <span className="skeleton boot__path" />
              </div>
              <div className="pane boot__page">
                <div className="boot__document">
                  <span className="skeleton boot__title" />
                  <span className="skeleton boot__meta" />
                  {[92, 100, 87, 96, 74].map((width, i) => (
                    <span key={i} className="skeleton boot__prose" style={{ width: `${width}%` }} />
                  ))}
                </div>
              </div>
            </main>
            <aside className="inspector boot__inspector">
              <span className="skeleton boot__inspector-title" />
              {[100, 82, 100, 66].map((width, i) => (
                <span
                  key={i}
                  className="skeleton boot__inspector-row"
                  style={{ width: `${width}%` }}
                />
              ))}
            </aside>
          </div>
        </div>
      </div>
    )
  }

  if (error.kind === 'provisioning') {
    // The 503 `ensureProjectFor` answers with when creating the repository
    // failed. It is real and it is not permanent — a GitHub rate limit, a
    // revoked token, a name collision — so this says so and offers to try
    // the same thing again, rather than the generic "is anything running at
    // all" copy below, which would send someone hunting for a dev server
    // that is running just fine.
    return (
      <div className="gate">
        <span className="gate__mark" style={{ background: 'var(--danger)' }}>
          <Icon name="alert" size={24} />
        </span>
        {/* A sentence, so it says so: `.gate h1` on its own is the wordmark
            rule, and it painted this "we could not set up your workspace". */}
        <h1 className="gate__title">We could not set up your workspace</h1>
        <p className="gate__text">The workspace is still being prepared. Try again in a moment.</p>
        <p className="gate__text">
          Nothing was lost — your account exists and no repository was half-created. This is usually
          worth trying again.
        </p>
        <div className="gate__retry">
          <button className="btn btn--primary" onClick={onRetry}>
            Try again
          </button>
          <button className="btn btn--ghost" onClick={onReset}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  if (error.kind === 'draft_unavailable') {
    return (
      <div className="gate">
        <div className="gate__identity">
          <span className="gate__mark" style={{ background: 'var(--danger)' }}>
            <Icon name="alert" size={24} />
          </span>
          <h1>Draft unavailable</h1>
        </div>
        <div className="gate__card">
          <h2>This draft is not available in this site</h2>
          <p className="gate__text">
            The review link points to a draft that was deleted, belongs to another documentation
            site, or has not been created yet.
          </p>
          <div className="gate__retry">
            <button className="btn btn--primary" onClick={onReset}>
              Return to sign in
            </button>
            <button className="btn btn--ghost" onClick={onRetry}>
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (error.kind === 'no_workspace') {
    /**
     * Signed in, and in no workspace.
     *
     * The way here is being removed from the only workspace somebody belonged
     * to, which is ordinary for an invited teammate: they joined somebody
     * else's and never owned one. Their address cannot sign up again — it is
     * taken — so without a door out of this screen the account is finished,
     * and the screen it used to get said the server could not be reached.
     */
    return (
      <div className="gate">
        <div className="gate__identity">
          <span className="gate__mark">
            <Icon name="logo" size={26} />
          </span>
          <h1>Staleless</h1>
        </div>
        <GateShowcase />
        <div className="gate__card">
          <p className="gate__text" data-no-workspace>
            You are not in any workspace. Somebody can invite you to theirs, or you can start one of
            your own.
          </p>
          <form
            className="gate__form gate__form--stacked"
            onSubmit={(e) => {
              e.preventDefault()
              void create(organisation.trim())
            }}
          >
            <input
              autoFocus
              className="input"
              data-new-workspace
              value={organisation}
              placeholder="Organisation"
              onChange={(e) => setOrganisation(e.target.value)}
            />
            {creating.error && (
              <p className="gate__error" role="alert">
                {creating.error}
              </p>
            )}
            <button className="btn btn--primary" disabled={creating.busy} type="submit">
              {creating.busy ? 'One moment…' : 'Create workspace'}
            </button>
          </form>
          <button className="btn btn--ghost btn--sm" onClick={onReset} type="button">
            Sign out
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="gate">
      <span className="gate__mark" style={{ background: 'var(--danger)' }}>
        <Icon name="alert" size={24} />
      </span>
      <h1 className="gate__title">We couldn’t load this workspace</h1>
      <p className="gate__text">
        Something interrupted the connection. Check your connection and try again.
      </p>
      <div className="gate__retry">
        <button className="btn btn--primary" onClick={onRetry}>
          Try again
        </button>
        <button className="btn btn--ghost" onClick={onReset}>
          Sign out
        </button>
      </div>
    </div>
  )
}

/**
 * What the sign-in screen shows beside the form.
 *
 * The gate used to be a form centred in an empty page, which reads as a
 * placeholder rather than a product — the same complaint anybody has about a
 * login box floating in grey. The half of the screen that is not a form is the
 * only chance this product gets to say what it is before somebody has an
 * account, so it says the one thing that distinguishes it: every page carries
 * the date it was last true, and the state is on the page, not in a report.
 *
 * Three real pages, not three wireframes. The first version drew grey bars,
 * which tell the reader that documentation contains lines of text — the one
 * thing they already knew. Prime real estate on the screen every stranger
 * sees, spent saying nothing. What is on them now is a page anybody with
 * documentation recognises, with the verdict this product exists to put on it,
 * and the last of the three names a pull request: the claim that the state
 * comes from the code, which no amount of grey bars can make.
 *
 * Straight, not fanned. The cards were rotated seven degrees as a group with
 * two of them nudged sideways, and it read as three divs that had been rotated
 * because something needed to look interesting. Nothing else in this product
 * is at an angle. The progression that the tilt was reaching for is in the
 * content instead — two days, ninety-three days, a pull request — which is a
 * progression somebody can read rather than one they can only feel.
 *
 * Deliberately NOT the lime. BRAND.md reserves the accent for action and
 * state, and a screen-half of it would be decoration — the largest possible
 * breach of the one rule the palette has. What carries the colour here is
 * Pulse, which is state, and which the tokens file names as the memorable
 * colour precisely because every developer tool is already blue.
 *
 * Marked aria-hidden: it is a picture of the product, and a screen reader
 * arriving at the sign-in form wants the form.
 */
const GATE_DOCS = [
  {
    key: 'current',
    title: 'Authentication',
    said: 'Updated 2 days ago',
    label: 'Current',
  },
  {
    key: 'review',
    title: 'Workspace permissions',
    said: 'Last reviewed 93 days ago',
    label: 'Review due',
  },
  {
    key: 'outdated',
    title: 'API tokens',
    /*
     * The claim, in six words.
     *
     * The other two states are things a calendar can work out. This one is
     * not: a page went out of date because a pull request changed the code it
     * describes, and nothing else on this screen says that Staleless knows
     * about the repository at all. It is the sentence the whole product is
     * for, so it is on the card the eye finishes on.
     */
    said: 'PR #482 changed this',
    label: 'Outdated',
  },
] as const

function GateShowcase() {
  return (
    <aside className="gate__showcase" aria-hidden="true">
      <div className="gate__stage">
        {GATE_DOCS.map((doc) => (
          <article key={doc.key} className={`gate-doc gate-doc--${doc.key}`}>
            <div className="gate-doc__side">
              <span className="gate-doc__brand" />
              <span className="gate-doc__nav" />
              <span className="gate-doc__nav gate-doc__nav--on" />
              <span className="gate-doc__nav" />
              <span className="gate-doc__nav" />
              <span className="gate-doc__nav" />
            </div>
            <div className="gate-doc__body">
              {/*
                A real title, and a real thing said about it. Three cards of
                grey bars say that documentation is made of lines of text,
                which the reader knew before arriving; what they do not know
                is that every page here carries a verdict, and the only way to
                show that is to write one.

                A span rather than a heading: this is a picture of a product,
                not an outline of this document, and the sign-in form owns the
                headings on this screen.
              */}
              <span className="gate-doc__title">{doc.title}</span>
              <span className="gate-doc__line" />
              <span className="gate-doc__line" />
              <span className="gate-doc__line gate-doc__line--short" />
              <div className="gate-doc__foot">
                <span className="gate-doc__said">{doc.said}</span>
                <span className="gate-doc__pulse">
                  <i className="gate-doc__mark" />
                  {doc.label}
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>
      <p className="gate__caption">Every page carries the date it was last true.</p>
    </aside>
  )
}
